import React, { useState, useEffect, useRef } from "react";
import { db, auth } from "./config/firebaseConfig"; // Add functions import
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  limit,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Dot,
  ReferenceLine,
} from "recharts";
import EditClientForm from "./EditClientForm";
import Navbar from "./Navbar";
import Footer from "./Footer";
import MessageModal from "./MessageModal";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "./config/firebaseConfig";

const TherapistDashboard = ({ therapistId }) => {
  // *******************************************************************************************
  // ******************************* CHECK IF USER LOGGED IN ***********************************
  // *******************************************************************************************
  const navigate = useNavigate();
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/login");
      }
      if (user) {
        fetchSecretKey(auth.currentUser?.uid);
        fetchClients();
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noClient, setnoClient] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null); // For editing client
  const [isEditing, setIsEditing] = useState(false); // Modal visibility
  const [isMessaging, setIsMessaging] = useState(false); // Message modal visibility
  const [therapistSecretKey, setTherapistSecretKey] = useState("");
  const [unreadMessages, setUnreadMessages] = useState({});
  const [clientScores, setClientScores] = useState({});

  //*** 🖥️ Inline Styles for Modal and Form ***
  const modalStyles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      zIndex: 9999,
    },
    content: {
      backgroundColor: "#2e2e2e",
      padding: "30px",
      width: "90%",
      maxWidth: "500px",
      borderRadius: "10px",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
      color: "#f0f0f0",
      fontSize: "12px",
    },
    inputGroup: {
      display: "flex",

      marginBottom: "5px",
    },
    input: {
      width: "230px",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #ccc",
      backgroundColor: "#575656",
      color: "#fff",
      fontSize: "12px",
    },
    textarea: {
      width: "95%",
      padding: "12px",
      marginBottom: "5px",
      borderRadius: "8px",
      border: "1px solid #ccc",
      backgroundColor: "#575656",
      color: "#fff",
      fontSize: "12px",
      resize: "vertical", // Allows vertical resizing of textareas
    },
    button: {
      backgroundColor: "#4caf50",
      padding: "12px 20px",
      borderRadius: "8px",
      color: "#fff",
      fontSize: "12px",
      cursor: "pointer",
      transition: "background-color 0.3s ease",
    },

    cancelButton: {
      backgroundColor: "#555",
    },
    buttonContainer: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: "20px",
    },
    motivationalText: {
      color: "white", // Set text color to yellow
      fontSize: "12px", // Adjust font size
      marginBottom: "5px", // Add some space below the text
      textAlign: "left", // Center the text
    },
    historyEntry: {
      cursor: "pointer",
    },
    historyEntryHover: {
      color: "#61fa69", // Text color changes to blue on hover
    },
  };

  // *****************************************************************
  // ********** Fetch clients associated with this therapist *********
  // *****************************************************************
  const fetchClients = async () => {
    setLoading(true);
    try {
      /* if (!therapistId) {
        throw new Error('Therapist ID is undefined.');
      } */
      const therapistId = auth.currentUser?.uid;
      if (!therapistId) {
        throw new Error(
          "Therapist ID is undefined. Please ensure you are logged in."
        );
      }

      const clientsQuery = query(
        collection(db, "users"),
        where("therapistId", "in", [therapistId, "demo"]),
        where("role", "==", "client")
      );

      const querySnapshot = await getDocs(clientsQuery);
      const clientList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClients(clientList);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setError("Failed to load clients. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [selectedClientId, setSelectedClientId] = useState(null);

  // Open edit modal
  const openEditModal = (client) => {
    setSelectedClient(client);
    setIsEditing(true);
  };

  // Close modals
  const closeEditModal = () => {
    setSelectedClient(null);
    setIsEditing(false);
  };

  const closeMessagingModal = () => {
    setSelectedClient(null);
    setIsMessaging(false);
  };

  // Function to fetch the secret key for the currently registered therapist
  const fetchSecretKey = async (userId) => {
    try {
      const docRef = doc(db, "users", userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setTherapistSecretKey(docSnap.data().therapistSecretKey);
      }
    } catch (error) {
      console.error("Error fetching secret key:", error);
    }
  };

  // Move hooks outside of the callback function
  const [userId, setUserId] = useState(null);

  // Set userId based on the authenticated user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  const [feedback, setFeedback] = useState("");
  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) {
      alert("Please provide your feedback before submitting.");
      return;
    }
    setLoading(true);
    try {
      const feedbackDocRef = doc(db, "therapistFeedback", therapistId);
      await setDoc(
        feedbackDocRef,
        {
          therapistId,
          feedback,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      // setIsSubmitted(true);
      alert(
        "Thank you for your feedback! We will review it shortly. We value your input."
      );
      setFeedback(""); // Clear the input
    } catch (error) {
      console.error("Error submitting feedback:", error);
      alert("Failed to submit feedback. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 2. Add a function to fetch scores for all clients
  const fetchAllClientScores = async () => {
    if (!clients.length) return;

    const scores = {};

    try {
      // Fetch scores for each client
      for (const client of clients) {
        const userDocRef = doc(db, "YBOCS_assessment", client.id);
        const formsCollectionRef = collection(userDocRef, "YBOCS_forms");

        // Query to get the most recent form
        const q = query(
          formsCollectionRef,
          orderBy("timestamp", "desc"),
          limit(1)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const data = querySnapshot.docs[0].data();

          // Check if scores field exists and is structured as expected
          if (data.scores && data.scores.total !== undefined) {
            scores[client.id] = data.scores.total;
          } else {
            scores[client.id] = "N/A";
          }
        } else {
          scores[client.id] = "N/A";
        }
      }

      setClientScores(scores);
    } catch (error) {
      console.error("Error fetching client scores:", error);
    }
  };

  // 3. Update useEffect to call the fetchAllClientScores function when clients are loaded
  useEffect(() => {
    if (clients.length > 0) {
      fetchAllClientScores();
    }
  }, [clients]);

  //******************* 📜 Messages *******************
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [receiverId, setReceiverId] = useState(null);

  //*** 🖥️ Fetch messages from Firestore ***
  const fetchMessages = async (receiverId) => {
    setReceiverId(receiverId);
    setMessages([]);

    const messagesRef = collection(db, "messages");
    const q = query(
      messagesRef,
      where("senderId", "in", [userId, receiverId]),
      where("receiverId", "in", [userId, receiverId]),
      orderBy("timestamp"),
      limit(20)
    );

    const querySnapshot = await getDocs(q);
    const fetchedMessages = querySnapshot.docs.map((doc) => doc.data());
    setMessages(fetchedMessages);
  };

  //*** 🖥️ Handle message input change ***
  const handleInputChangeGoal = (e) => {
    setNewMessage(e.target.value);
  };

  //*** 🖥️ Send a new message ***
  const sendMessage = async () => {
    if (newMessage.trim() !== "") {
      const senderId = auth.currentUser?.uid;
      const timestamp = new Date();

      const message = {
        senderId: senderId,
        receiverId: receiverId,
        message: newMessage,
        timestamp: timestamp,
        messageType: "therapist", // or 'therapist' depending on the sender
      };

      await addDoc(collection(db, "messages"), message);
      setNewMessage("");
      fetchMessages(receiverId);
    }
  };

  // *** 🖥️ Load journal entry ***
  const [journalEntries, setJournalEntries] = useState([]);
  const emojiOptions = {
    0: "Happy", //
    1: "Slightly happy", //
    2: "Worried", //
    3: "Very distressed", //
  };

  const loadJournalEntries = async (userId) => {
    setJournalEntries([]);
    try {
      const userDoc = await getDoc(doc(db, "journals", userId));
      if (userDoc.exists()) {
        const journalData = userDoc.data().entries || {};
        const loadedEntries = [];

        Object.keys(journalData).forEach((date) => {
          const dateEntries = journalData[date];

          Object.keys(dateEntries).forEach((entryId) => {
            const entry = dateEntries[entryId];
            const emoji = emojiOptions[entry.score] || "😊";

            loadedEntries.push({
              date,
              entryId,
              text: entry.text,
              emoji,
              score: entry.score,
              timestamp: entry.timestamp,
            });
          });
        });

        loadedEntries.sort((a, b) => b.timestamp - a.timestamp);
        setJournalEntries(loadedEntries);
      }
    } catch (error) {
      console.error("Error loading journal entries:", error);
    }
  };

  //*** 🖥️ Fecth coupons ***
  const [history, setHistory] = useState([]);
  const fetchCoupons = async (userId) => {
    const userHistoryDoc = await getDoc(doc(db, "couponHistory", userId));
    if (userHistoryDoc.exists()) {
      setHistory(userHistoryDoc.data().history || []);
    } else {
      setHistory([]);
    }
  };

  //*** 🖥️ Fecth asssesments ***
  const [scores, setScores] = useState({ totalScore: 0 });
  const [scoresPHQ9, setScoresPHQ9] = useState({ totalScore: 0 });
  const [scoresGAD7, setScoresGAD7] = useState({ totalScore: 0 });

  const fetchAssessments = async (userId) => {
    setScores({ totalScore: 0 });
    setScoresPHQ9({ totalScore: 0 });
    setScoresGAD7({ totalScore: 0 });
    const userDocRef = doc(db, "YBOCS_assessment", userId); // Reference to the user document
    const formsCollectionRef = collection(userDocRef, "YBOCS_forms"); // Sub-collection for the forms

    try {
      // Query the sub-collection to get all forms, ordered by timestamp in descending order
      const q = query(formsCollectionRef, orderBy("timestamp", "desc")); // 'desc' for most recent first
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const formsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();

          // Check if 'scores' field exists and is structured as expected
          if (data.scores && data.scores.total !== undefined) {
            formsData.push({
              timestamp: data.timestamp.toDate(),
              totalScore: data.scores.total,
              compulsions: data.scores.compulsions,
              obsessions: data.scores.obsessions,
            });
          }
        });

        if (formsData.length > 0) {
          const mostRecentForm = formsData[0]; // Most recent form
          setScores({ totalScore: mostRecentForm.totalScore }); // Update state with the total score
        } else {
          console.warn("No valid forms with scores found.");
        }
      } else {
        console.warn("No forms found in the database.");
      }
    } catch (error) {
      console.error("Error fetching YBOCS forms:", error);
    }

    const userDocRefPHQ9 = doc(db, "PHQ9_assessment", userId); // Reference to the user document
    const formsCollectionRefPHQ9 = collection(userDocRefPHQ9, "PHQ9_forms"); // Sub-collection for the forms

    try {
      const qPHQ9 = query(formsCollectionRefPHQ9, orderBy("timestamp", "desc")); // 'desc' for most recent first
      const querySnapshotPHQ9 = await getDocs(qPHQ9);

      if (!querySnapshotPHQ9.empty) {
        const formsDataPHQ9 = [];
        querySnapshotPHQ9.forEach((doc) => {
          const data = doc.data();

          // Check if 'scores' field exists and is structured as expected
          if (data.scores && data.scores.totalScore !== undefined) {
            formsDataPHQ9.push({
              timestamp: data.timestamp.toDate(),
              totalScore: data.scores.totalScore,
            });
          }
        });

        if (formsDataPHQ9.length > 0) {
          const mostRecentForm = formsDataPHQ9[0]; // Most recent form
          setScoresPHQ9({ totalScore: mostRecentForm.totalScore }); // Update state with the total score
        } else {
          console.warn("No valid forms with scores found.");
        }
      } else {
        console.warn("No PHQ-9 forms found in the database.");
      }
    } catch (error) {
      console.error("Error fetching PHQ-9 forms:", error);
    }

    const userDocRefGAD7 = doc(db, "GAD7_assessment", userId); // Reference to the user document
    const formsCollectionRefGAD7 = collection(userDocRefGAD7, "GAD7_forms"); // Sub-collection for the forms

    try {
      const qGAD7 = query(formsCollectionRefGAD7, orderBy("timestamp", "desc")); // 'desc' for most recent first
      const querySnapshotGAD7 = await getDocs(qGAD7);

      if (!querySnapshotGAD7.empty) {
        const formsDataGAD7 = [];
        querySnapshotGAD7.forEach((doc) => {
          const data = doc.data();

          // Check if 'scores' field exists and is structured as expected
          if (data.scores && data.scores.totalScore !== undefined) {
            formsDataGAD7.push({
              timestamp: data.timestamp.toDate(),
              totalScore: data.scores.totalScore,
            });
          }
        });

        if (formsDataGAD7.length > 0) {
          const mostRecentForm = formsDataGAD7[0]; // Most recent form
          setScoresGAD7({ totalScore: mostRecentForm.totalScore }); // Update state with the total score
        } else {
          console.warn("No valid forms with scores found.");
        }
      } else {
        console.warn("No GAD7 forms found in the database.");
      }
    } catch (error) {
      console.error("Error fetching PHQ-9 forms:", error);
    }
  };

  // *** 🖥️ Exposure hierarchy builder ***
  const [exposures, setExposures] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentExposureTitle, setCurrentExposureTitle] = useState("");
  const [exposureData, setExposureData] = useState({
    exposurePlan: "",
    fearedOutcome: "",
    responsePrevention: "",
    fearBelief: 0,
    confidence: 0,
    exposureOutcome: "",
    exposureVariation: "",
  });
  const [previewURL, setPreviewURL] = useState(null);
  const audioRef = useRef(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTimer, setRecordingTimer] = useState(0);
  const [audioChunks, setAudioChunks] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const distressColors = {
    1: "#61fa69", // Green for lowest distress
    2: "#76fa5e",
    3: "#8afa53",
    4: "#9efa47",
    5: "#aefa3c",
    6: "#c0d32d",
    7: "#d3b22f",
    8: "#e89c2a",
    9: "#f58c24",
    10: "#fa6161", // Red for highest distress
    null: "#fa6161",
  };

  const isRestrictedClient =
    selectedClientId === "ADjKRnX1iXduMuFV3GRmpjnAk7m1";

  //*** 🖥️ distress tolerance skills
  // 1. Add new state variables for distress tolerance features
  const [distressSkillsModalVisible, setDistressSkillsModalVisible] =
    useState(false);
  const [selectedExposureForSkills, setSelectedExposureForSkills] =
    useState(null);
  const [distressSkills, setDistressSkills] = useState([]);
  const [newSkill, setNewSkill] = useState({
    title: "",
    description: "",
    type: "breathing", // Default type
    audioURL: null,
    order: 0,
  });
  const [audioFile, setAudioFile] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);

  // 2. Add a function to handle file selection
  const handleAudioFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log("Selected file:", file.name, file.type, file.size);

      // Check if it's an audio file
      if (!file.type.startsWith("audio/")) {
        window.alert("Please select an audio file");
        return;
      }

      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        window.alert("Please select an audio file smaller than 5MB");
        return;
      }

      setAudioFile(file);

      // Create a preview URL for the file
      const url = URL.createObjectURL(file);
      setPreviewURL(url);
      setAudioBlob(file);
    }
  };

  // Add this utility function
  const uploadWithRetry = async (
    storageRef,
    file,
    metadata,
    maxRetries = 3
  ) => {
    let lastError;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await uploadBytes(storageRef, file, metadata);
      } catch (error) {
        console.error(`Upload attempt ${attempt + 1} failed:`, error);
        lastError = error;
        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
    throw lastError; // If all retries failed, throw the last error
  };

  // 2. Function to open the distress skills modal for a specific exposure
  const openDistressSkillsModal = async (exposure) => {
    setSelectedExposureForSkills(exposure);
    setDistressSkillsModalVisible(true);
    setNewSkill({
      title: "",
      description: "",
      type: "breathing",
      audioURL: null,
      order: 0,
    });

    // Load existing distress skills for this exposure
    await loadDistressSkills(exposure.id);
  };

  // 3. Function to load distress skills for an exposure
  const loadDistressSkills = async (exposureId) => {
    setDistressSkills([]);
    try {
      // Reference to subcollection of distress skills for this exposure
      const skillsRef = collection(
        db,
        "exposureHierarchy",
        selectedClientId,
        "DistressSkills"
      );
      const q = query(
        skillsRef,
        where("exposureId", "==", exposureId),
        orderBy("order", "asc")
      );

      const snapshot = await getDocs(q);
      const skills = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setDistressSkills(skills);
    } catch (error) {
      console.error("Error loading distress skills:", error);
    }
  };

  // 3. Modify the saveDistressSkill function to handle file upload
  const saveDistressSkill = async () => {
    if (!newSkill.title.trim()) {
      window.alert("Please provide a title for this skill");
      return;
    }

    setLoadingAudio(true);
    try {
      console.log("Starting to save distress skill");

      // Create a base skill object
      const skillData = {
        exposureId: selectedExposureForSkills.id,
        exposureTitle: selectedExposureForSkills.title,
        title: newSkill.title,
        description: newSkill.description,
        type: newSkill.type,
        created: serverTimestamp(),
        order: distressSkills.length + 1,
      };

      // If there's an uploaded audio file, upload it to storage
      if (audioFile) {
        console.log("Uploading audio file:", audioFile.name);

        try {
          // Create a unique filename with a safe format
          const timestamp = Date.now();
          const randomString = Math.random().toString(36).substring(2, 15);
          const fileExtension = audioFile.name.split(".").pop().toLowerCase();
          const filename = `${timestamp}_${randomString}.${fileExtension}`;

          // Create a clean storage path - avoiding characters that might cause issues
          const storagePath = `distressSkills/${selectedClientId}/${timestamp}/${filename}`;

          console.log("Storage path:", storagePath);
          const storageRef = ref(storage, storagePath);

          // Upload as an ArrayBuffer to avoid encoding issues
          const fileArrayBuffer = await audioFile.arrayBuffer();

          // Upload with specific content type
          const metadata = {
            contentType: audioFile.type,
          };

          // Upload the file
          const uploadResult = await uploadWithRetry(
            storageRef,
            fileArrayBuffer,
            metadata
          );
          console.log("Upload successful:", uploadResult);

          // Get download URL
          const downloadURL = await getDownloadURL(uploadResult.ref);
          console.log("Download URL obtained:", downloadURL);

          // Add the audio URL to the skill data
          skillData.audioURL = downloadURL;
        } catch (uploadError) {
          console.error("Error during file upload:", uploadError);
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
      }

      console.log("Saving skill data to Firestore:", skillData);

      // Add the skill to Firestore
      const skillsRef = collection(
        db,
        "exposureHierarchy",
        selectedClientId,
        "DistressSkills"
      );
      const docRef = await addDoc(skillsRef, skillData);

      console.log("Skill saved with ID:", docRef.id);

      // Reset form and reload skills
      setNewSkill({
        title: "",
        description: "",
        type: "breathing",
        audioURL: null,
        order: 0,
      });
      setAudioFile(null);
      setAudioBlob(null);
      if (previewURL) {
        URL.revokeObjectURL(previewURL);
        setPreviewURL(null);
      }

      await loadDistressSkills(selectedExposureForSkills.id);

      window.alert("Distress tolerance skill added successfully");
    } catch (error) {
      console.error("Error saving distress skill:", error);
      window.alert(`Failed to save the skill: ${error.message}`);
    } finally {
      setLoadingAudio(false);
    }
  };
  // 5. Function to delete a distress skill
  const deleteDistressSkill = async (skillId) => {
    if (!window.confirm("Are you sure you want to delete this skill?")) return;

    try {
      // Find the skill to get its audio URL if it exists
      const skillToDelete = distressSkills.find(
        (skill) => skill.id === skillId
      );

      // Delete the document from Firestore
      await deleteDoc(
        doc(
          db,
          "exposureHierarchy",
          selectedClientId,
          "DistressSkills",
          skillId
        )
      );

      // If there was an audio file, delete it from storage too
      if (skillToDelete?.audioURL) {
        try {
          // Extract the path from the URL
          const url = new URL(skillToDelete.audioURL);
          const storagePath = url.pathname.split("/o/")[1]?.split("?")[0];

          if (storagePath) {
            const decodedPath = decodeURIComponent(storagePath);
            const fileRef = ref(storage, decodedPath);
            await deleteObject(fileRef);
          }
        } catch (storageError) {
          console.error("Error deleting audio file:", storageError);
          // Continue with the skill deletion even if audio deletion fails
        }
      }

      // Reload the skills list
      await loadDistressSkills(selectedExposureForSkills.id);
    } catch (error) {
      console.error("Error deleting distress skill:", error);
      alert("Failed to delete the skill. Please try again.");
    }
  };

  // 6. Function to start audio recording
  const startRecording = async () => {
    setAudioChunks([]);
    setIsRecording(true);
    setRecordingTimer(0);

    // Clean up any previous preview URL
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
      setPreviewURL(null);
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      setMediaRecorder(recorder);

      // Collect audio chunks
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          setAudioChunks((chunks) => [...chunks, e.data]);
        }
      };

      // When recording stops, create a blob and URL
      recorder.onstop = () => {
        // Create a new array for the chunks to avoid state update issues
        const chunks = [...audioChunks, ...(recorder.chunks || [])];
        const audioBlob = new Blob(chunks, { type: "audio/webm; codecs=opus" });
        setAudioBlob(audioBlob);

        // Create a URL for the audio preview
        const url = URL.createObjectURL(audioBlob);
        setPreviewURL(url);

        setIsRecording(false);

        // Stop all tracks in the stream to release the microphone
        stream.getTracks().forEach((track) => track.stop());
      };

      // Start recording
      recorder.start();

      // Start timer
      const timerInterval = setInterval(() => {
        setRecordingTimer((prev) => {
          // Auto-stop after 2 minutes (120 seconds)
          if (prev >= 120) {
            clearInterval(timerInterval);
            stopRecording();
            return 120;
          }
          return prev + 1;
        });
      }, 1000);

      // Save the interval ID for cleanup
      recorder.timerInterval = timerInterval;
    } catch (error) {
      console.error("Error starting recording:", error);
      alert(
        "Could not access microphone. Please check your browser permissions."
      );
      setIsRecording(false);
    }
  };

  // 4. Create a function to play the preview correctly
  const playPreview = () => {
    if (audioRef.current && previewURL) {
      if (audioRef.current.paused) {
        audioRef.current.play().catch((err) => {
          console.error("Error playing audio:", err);
          alert("Could not play audio preview. Please try recording again.");
        });
      } else {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  };

  // 5. Add cleanup for audio resources in a useEffect
  useEffect(() => {
    // Cleanup function for audio preview URL
    return () => {
      if (previewURL) {
        URL.revokeObjectURL(previewURL);
      }
    };
  }, [previewURL]);

  // 7. Function to stop audio recording
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();

      // Clear the timer interval
      if (mediaRecorder.timerInterval) {
        clearInterval(mediaRecorder.timerInterval);
      }

      // Note: We'll create the audio URL when the ondataavailable event completes
    }
  };

  // 8. Function to play audio preview
  const playAudio = (audioURL, id) => {
    // Stop any currently playing audio
    if (playingAudioId) {
      const audioElement = document.getElementById(`audio-${playingAudioId}`);
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    }

    // Play the selected audio
    const audioElement = document.getElementById(`audio-${id}`);
    if (audioElement) {
      audioElement.play();
      setPlayingAudioId(id);

      // Reset playing ID when audio ends
      audioElement.onended = () => setPlayingAudioId(null);
    }
  };

  // 9. Function to format time for recording display
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // 10. Create the distress skills modal
  const distressSkillsModal = (
    <div style={modalStyles.overlay}>
      <div
        style={{
          ...modalStyles.content,
          maxWidth: "800px",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <h2 style={{ color: "#f09a73", marginTop: "0px" }}>
          <span style={{ marginRight: "10px" }}>🧘</span>
          Distress Tolerance Skills: {selectedExposureForSkills?.title}
        </h2>

        <p style={{ fontSize: "14px", color: "#ccc", marginBottom: "20px" }}>
          Add resources to help your client manage anxiety during this exposure.
          These can include breathing exercises, grounding techniques, or
          personalized audio instructions.
        </p>

        {/* Existing Skills Section */}
        <div style={{ marginBottom: "30px" }}>
          <h3
            style={{
              fontSize: "16px",
              borderBottom: "1px solid #444",
              paddingBottom: "8px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "8px" }}>📚</span>
            Existing Skills
          </h3>

          {distressSkills.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginTop: "15px",
              }}
            >
              {distressSkills.map((skill) => (
                <div
                  key={skill.id}
                  style={{
                    backgroundColor: "#333",
                    padding: "15px",
                    borderRadius: "8px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                    }}
                  >
                    <button
                      onClick={() => deleteDistressSkill(skill.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#f44336",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                      title="Delete this skill"
                    >
                      🗑️
                    </button>
                  </div>

                  <h4
                    style={{
                      margin: "0 0 10px 0",
                      color: "#4caf50",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {skill.type === "breathing" && (
                      <span style={{ marginRight: "8px" }}>🫁</span>
                    )}
                    {skill.type === "grounding" && (
                      <span style={{ marginRight: "8px" }}>🌿</span>
                    )}
                    {skill.type === "cognitive" && (
                      <span style={{ marginRight: "8px" }}>🧠</span>
                    )}
                    {skill.type === "custom" && (
                      <span style={{ marginRight: "8px" }}>✨</span>
                    )}
                    {skill.title}
                  </h4>

                  <p
                    style={{
                      margin: "0 0 15px 0",
                      fontSize: "14px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {skill.description}
                  </p>

                  {skill.audioURL && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px",
                        backgroundColor: "#222",
                        borderRadius: "6px",
                      }}
                    >
                      <button
                        onClick={() => playAudio(skill.audioURL, skill.id)}
                        style={{
                          background: "#4caf50",
                          border: "none",
                          borderRadius: "50%",
                          width: "36px",
                          height: "36px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        {playingAudioId === skill.id ? "⏸️" : "▶️"}
                      </button>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "12px", color: "#aaa" }}>
                          Audio Prompt
                        </div>
                        <audio
                          id={`audio-${skill.id}`}
                          src={skill.audioURL}
                          style={{ display: "none" }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ textAlign: "center", color: "#aaa" }}>
              No distress tolerance skills have been added yet for this
              exposure.
            </p>
          )}
        </div>

        {/* Add New Skill Form */}
        <div
          style={{
            backgroundColor: "#2a2a2a",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              marginTop: "0",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "8px" }}>➕</span>
            Add New Skill
          </h3>

          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Skill Title*:
            </label>
            <input
              type="text"
              style={{
                ...modalStyles.input,
                width: "95%",
              }}
              value={newSkill.title}
              onChange={(e) =>
                setNewSkill({ ...newSkill, title: e.target.value })
              }
              placeholder="e.g., Deep Breathing Exercise, 5-4-3-2-1 Grounding"
              required
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Skill Type:
            </label>
            <select
              style={{
                ...modalStyles.input,
                width: "95%",
              }}
              value={newSkill.type}
              onChange={(e) =>
                setNewSkill({ ...newSkill, type: e.target.value })
              }
            >
              <option value="breathing">Breathing Exercise</option>
              <option value="grounding">Grounding Technique</option>
              <option value="cognitive">Cognitive Strategy</option>
              <option value="custom">Custom Approach</option>
            </select>
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Instructions/Description:
            </label>
            <textarea
              style={{
                ...modalStyles.textarea,
                height: "100px",
              }}
              value={newSkill.description}
              onChange={(e) =>
                setNewSkill({ ...newSkill, description: e.target.value })
              }
              placeholder="Provide detailed instructions for this skill. For example: 'Breathe in deeply through your nose for a count of 4, hold for 4 counts, then exhale slowly through your mouth for 6 counts...'"
            />
          </div>

          {/* Audio Upload Section 
		<div style={{ 
		  marginBottom: "15px",
		  padding: "15px",
		  backgroundColor: "#222",
		  borderRadius: "8px"
		}}>
		  <h4 style={{ 
			fontSize: "14px", 
			margin: "0 0 10px 0",
			display: "flex",
			alignItems: "center"
		  }}>
			<span style={{ marginRight: "8px" }}>🔊</span>
			Upload Audio File (Optional)
		  </h4>
		  
		  <p style={{ fontSize: "12px", color: "#aaa", marginBottom: "15px" }}>
			Upload an audio file to help your client during the exposure.
			This could be a guided meditation, relaxation technique, or personalized instructions.
			(Max size: 5MB, supported formats: MP3, WAV, OGG)
		  </p>
		  
		  {!audioBlob ? (
			<div style={{
			  display: "flex",
			  flexDirection: "column",
			  alignItems: "center",
			  gap: "10px"
			}}>
			  <label 
				htmlFor="audio-upload" 
				style={{
				  backgroundColor: "#4caf50",
				  color: "white",
				  border: "none",
				  borderRadius: "6px",
				  padding: "10px 20px",
				  cursor: "pointer",
				  display: "flex",
				  alignItems: "center",
				  gap: "8px"
				}}
			  >
				<span>Select Audio File</span>
				<span>📁</span>
			  </label>
			  <input 
				id="audio-upload" 
				type="file" 
				accept="audio/*" 
				onChange={handleAudioFileChange} 
				style={{ display: "none" }}
			  />
			</div>
		  ) : (
			<div style={{
			  display: "flex",
			  flexDirection: "column",
			  alignItems: "center",
			  gap: "15px"
			}}>
			  <div style={{
				display: "flex",
				alignItems: "center",
				gap: "10px",
				padding: "10px 15px",
				backgroundColor: "#333",
				borderRadius: "6px",
				width: "80%"
			  }}>
				<button
				  onClick={playPreview}
				  style={{
				    background: "#4caf50",
				    border: "none",
				    borderRadius: "50%",
				    width: "36px",
				    height: "36px",
				    display: "flex",
				    justifyContent: "center",
				    alignItems: "center",
				    cursor: "pointer"
				  }}
				>
				  {audioRef.current && !audioRef.current.paused ? '⏸️' : '▶️'}
				</button>
				<div style={{ flex: 1 }}>
				  <div style={{ fontSize: "14px" }}>Audio Preview</div>
				  <div style={{ fontSize: "12px", color: "#aaa" }}>
				    {audioFile ? audioFile.name : "Selected audio file"}
				  </div>
				</div>
				<button
				  onClick={() => {
				    if (previewURL) {
				      URL.revokeObjectURL(previewURL);
				      setPreviewURL(null);
				    }
				    setAudioBlob(null);
				    setAudioFile(null);
				  }}
				  style={{
				    background: "none",
				    border: "none",
				    color: "#f44336",
				    cursor: "pointer",
				    fontSize: "16px"
				  }}
				  title="Remove audio file"
				>
				  🗑️
				</button>
			  </div>
			  
			  <audio 
				ref={audioRef}
				src={previewURL} 
				onEnded={() => audioRef.current.currentTime = 0}
				style={{ display: "none" }}
			  />
			</div>
		  )}
		</div>
		    */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <button
              onClick={saveDistressSkill}
              disabled={!newSkill.title.trim() || loadingAudio}
              style={{
                ...modalStyles.button,
                width: "200px",
              }}
            >
              {loadingAudio ? "Saving..." : "Add Distress Tolerance Skill"}
            </button>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid #444",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <p style={{ fontSize: "14px", color: "#aaa", flex: 1 }}>
            These tools will be available to the client during their exposure
            exercises, helping them manage distress and complete exposures
            successfully.
          </p>

          <button
            onClick={() => setDistressSkillsModalVisible(false)}
            style={{ ...modalStyles.button, ...modalStyles.cancelButton }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  //*** 🖥️ Delete and edit exposures
  // 1. Add state variables for edit functionality
  const [editExposureModalVisible, setEditExposureModalVisible] =
    useState(false);
  const [exposureToEdit, setExposureToEdit] = useState(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [exposureToDelete, setExposureToDelete] = useState(null);

  // 2. Function to open the edit modal
  const openEditExposureModal = (exposure) => {
    setExposureToEdit({ ...exposure });
    setEditExposureModalVisible(true);
  };

  // 4. Function to confirm deletion
  const confirmDeleteExposure = (exposure) => {
    setExposureToDelete(exposure);
    setDeleteConfirmVisible(true);
  };

  // 5. Function to handle exposure deletion
  const handleDeleteExposure = async () => {
    if (!exposureToDelete || !exposureToDelete.id) return;

    setLoading(true);
    try {
      // Get reference to the client's exposures document
      const exposureDocRef = doc(db, "exposureHierarchy", selectedClientId);
      const docSnap = await getDoc(exposureDocRef);

      if (docSnap.exists()) {
        const existingExposures = docSnap.data().exposures || [];

        // Filter out the exposure with matching ID
        const updatedExposures = existingExposures.filter(
          (exp) => exp.id !== exposureToDelete.id
        );

        await updateDoc(exposureDocRef, {
          exposures: updatedExposures,
        });

        // Clean up any clinical notes for this exposure
        const notesRef = collection(
          db,
          "exposureHierarchy",
          selectedClientId,
          "Notes"
        );
        const notesQuery = query(
          notesRef,
          where("exposureId", "==", exposureToDelete.id)
        );

        const notesSnapshot = await getDocs(notesQuery);
        notesSnapshot.forEach(async (noteDoc) => {
          await deleteDoc(
            doc(db, "exposureHierarchy", selectedClientId, "Notes", noteDoc.id)
          );
        });

        // Close confirmation and reload exposures
        setDeleteConfirmVisible(false);
        loadExposures(selectedClientId);

        alert("Exposure successfully deleted");
      }
    } catch (error) {
      console.error("Error deleting exposure:", error);
      alert("Failed to delete exposure. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 7. Create the delete confirmation modal
  const deleteConfirmationModal = (
    <div style={modalStyles.overlay}>
      <div style={{ ...modalStyles.content, maxWidth: "400px" }}>
        <h2 style={{ color: "#f44336", marginTop: "0px" }}>Confirm Deletion</h2>

        <p style={{ marginBottom: "20px" }}>
          Are you sure you want to delete the exposure:
          <br />
          <strong style={{ color: "#f09a73" }}>
            {exposureToDelete?.title}
          </strong>
          ?
          <br />
          <br />
          This action cannot be undone. All logs and clinical notes for this
          exposure will also be deleted.
        </p>

        <div style={modalStyles.buttonContainer}>
          <button
            onClick={() => setDeleteConfirmVisible(false)}
            style={{ ...modalStyles.button, ...modalStyles.cancelButton }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteExposure}
            style={{ ...modalStyles.button, backgroundColor: "#f44336" }}
            disabled={loading}
          >
            {loading ? "Deleting..." : "Delete Exposure"}
          </button>
        </div>
      </div>
    </div>
  );

  //*** 🖥️  Clinical notes
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [selectedExposureForNotes, setSelectedExposureForNotes] =
    useState(null);
  const [clinicalNote, setClinicalNote] = useState("");
  const [existingNotes, setExistingNotes] = useState([]);

  // function to handle different timestamp formats
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown date";

    // Handle Firestore timestamp
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }

    // Handle JS Date object
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }

    // Handle ISO string
    return new Date(timestamp).toLocaleString();
  };

  // Function to open the notes modal for a specific exposure
  const openNotesModal = async (exposure) => {
    setSelectedExposureForNotes(exposure);
    setClinicalNote(""); // Clear the input field
    setNotesModalVisible(true);

    // Load existing notes for this exposure
    try {
      const notesRef = collection(
        db,
        "exposureHierarchy",
        selectedClientId,
        "Notes"
      );
      const notesQuery = query(
        notesRef,
        where("exposureId", "==", exposure.id),
        orderBy("timestamp", "desc")
      );

      const notesSnap = await getDocs(notesQuery);
      const notesData = notesSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setExistingNotes(notesData);
    } catch (error) {
      console.error("Error loading clinical notes:", error);
      setExistingNotes([]);
    }
  };

  // Function to save a new clinical note
  const saveClinicaNote = async () => {
    if (!clinicalNote.trim() || !selectedExposureForNotes) {
      alert("Please enter a note before saving");
      return;
    }

    setLoading(true);
    try {
      // Add the note to a "Notes" subcollection
      const notesRef = collection(
        db,
        "exposureHierarchy",
        selectedClientId,
        "Notes"
      );
      await addDoc(notesRef, {
        exposureId: selectedExposureForNotes.id,
        exposureTitle: selectedExposureForNotes.title,
        note: clinicalNote,
        timestamp: new Date(),
        therapistId: auth.currentUser?.uid,
        therapistName: auth.currentUser?.displayName || "Therapist",
      });

      // Refresh the notes list
      openNotesModal(selectedExposureForNotes);
      setClinicalNote(""); // Clear the input

      alert("Clinical note saved successfully");
    } catch (error) {
      console.error("Error saving clinical note:", error);
      alert("Failed to save note. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Create the clinical notes modal
  const clinicalNotesModal = (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.content}>
        <h2 style={{ color: "#f09a73", marginTop: "0px" }}>
          Clinical Notes: {selectedExposureForNotes?.title}
        </h2>

        <div style={{ marginBottom: "20px" }}>
          <h3>Add New Clinical Note</h3>
          <textarea
            style={{
              ...modalStyles.textarea,
              height: "100px",
            }}
            value={clinicalNote}
            onChange={(e) => setClinicalNote(e.target.value)}
            placeholder="Enter treatment rationale, observations, and next steps for this exposure..."
          />
          <button
            onClick={saveClinicaNote}
            style={{
              ...modalStyles.button,
              marginTop: "10px",
              width: "120px",
            }}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Note"}
          </button>
        </div>

        <div>
          <h3>Previous Notes</h3>
          <div
            style={{
              maxHeight: "300px",
              overflowY: "auto",
              backgroundColor: "#333",
              borderRadius: "8px",
              padding: "10px",
            }}
          >
            {existingNotes.length > 0 ? (
              existingNotes.map((note, index) => (
                <div
                  key={note.id}
                  style={{
                    backgroundColor: "#222",
                    padding: "10px",
                    marginBottom: "10px",
                    borderRadius: "5px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "5px",
                      fontSize: "10px",
                      color: "#aaa",
                    }}
                  >
                    <span>{formatTimestamp(note.timestamp)}</span>
                    <span>{note.therapistName}</span>
                  </div>
                  <p style={{ margin: "0", whiteSpace: "pre-wrap" }}>
                    {note.note}
                  </p>
                </div>
              ))
            ) : (
              <p style={{ textAlign: "center", color: "#aaa" }}>
                No clinical notes found for this exposure
              </p>
            )}
          </div>
        </div>

        <div style={modalStyles.buttonContainer}>
          <button
            onClick={() => setNotesModalVisible(false)}
            style={{ ...modalStyles.button, ...modalStyles.cancelButton }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  //*** 🖥️  Assign exposures to patients
  const [newExposureModalVisible, setNewExposureModalVisible] = useState(false);
  const [newExposure, setNewExposure] = useState({
    title: "",
    description: "",
    initialFearBelief: 5, // Default rating
    completed: false,
  });

  // Add this function to handle the form submission
  const handleAddExposure = async () => {
    if (!newExposure.title.trim()) {
      alert("Please provide a title for the exposure");
      return;
    }

    setLoading(true);
    try {
      // Get reference to the client's exposures document
      const exposureDocRef = doc(db, "exposureHierarchy", selectedClientId);
      const docSnap = await getDoc(exposureDocRef);

      // Create new exposure object with unique ID
      const newExposureItem = {
        id: Date.now().toString(),
        title: newExposure.title,
        description: newExposure.description,
        fearBelief: "5",
        completed: false,
        assignedAt: new Date().toISOString(), // Use ISO string instead
        assignedBy: auth.currentUser?.uid,
      };

      // Add to existing exposures or create new array
      if (docSnap.exists()) {
        const existingExposures = docSnap.data().exposures || [];
        await updateDoc(exposureDocRef, {
          exposures: [...existingExposures, newExposureItem],
        });
      } else {
        await setDoc(exposureDocRef, {
          exposures: [newExposureItem],
        });
      }

      // Clear form and close modal
      setNewExposure({
        title: "",
        description: "",
        initialFearBelief: 5,
        completed: false,
      });
      setNewExposureModalVisible(false);

      // Reload exposures
      loadExposures(selectedClientId);

      alert("Exposure successfully assigned to the patient");
    } catch (error) {
      console.error("Error adding exposure:", error);
      alert("Failed to assign exposure. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Create the modal for adding new exposures
  const newExposureModal = (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.content}>
        <h2 style={{ color: "#f09a73", marginTop: "0px" }}>
          Assign New Exposure
        </h2>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAddExposure();
          }}
        >
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Exposure Title*:
            </label>
            <input
              type="text"
              style={{
                ...modalStyles.input,
                width: "95%",
              }}
              value={newExposure.title}
              onChange={(e) =>
                setNewExposure({ ...newExposure, title: e.target.value })
              }
              placeholder="Enter exposure title"
              required
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Description:
            </label>
            <textarea
              style={modalStyles.textarea}
              value={newExposure.description}
              onChange={(e) =>
                setNewExposure({ ...newExposure, description: e.target.value })
              }
              placeholder="Enter detailed instructions for this exposure"
              rows="4"
            />
          </div>

          <div style={modalStyles.buttonContainer}>
            <button
              type="button"
              onClick={() => setNewExposureModalVisible(false)}
              style={{ ...modalStyles.button, ...modalStyles.cancelButton }}
            >
              Cancel
            </button>
            <button type="submit" style={modalStyles.button} disabled={loading}>
              {loading ? "Assigning..." : "Assign Exposure"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  //*** 🖥️  Progression Tracking Component

  const getFearColor = (level) => {
    const fearColors = {
      1: "rgba(60, 179, 113, 0.8)", // Green
      2: "rgba(85, 190, 90, 0.8)", // Light green
      3: "rgba(170, 219, 70, 0.8)", // Lime green
      4: "rgba(250, 237, 39, 0.8)", // Yellow
      5: "rgba(252, 211, 55, 0.8)", // Amber
      6: "rgba(247, 173, 35, 0.8)", // Orange
      7: "rgba(242, 120, 30, 0.8)", // Dark orange
      8: "rgba(235, 77, 35, 0.8)", // Red-orange
      9: "rgba(229, 43, 39, 0.8)", // Light red
      10: "rgba(224, 0, 26, 0.8)", // Red
    };

    return fearColors[level] || "#fa6161"; // Default to red if level is undefined
  };

  const ExposureProgressionTracker = ({ exposures, clientId }) => {
    // Group logs by exposure name and calculate averages over time
    const [progressionData, setProgressionData] = useState({});
    const [loading, setLoading] = useState(true);

    // Define all styles as JavaScript objects
    const styles = {
      tracker: {
        marginTop: "20px",
      },
      list: {
        display: "flex",
        flexDirection: "column",
        gap: "15px",
      },
      item: {
        backgroundColor: "#333",
        borderRadius: "8px",
        padding: "10px",
      },
      header: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "5px",
      },
      title: {
        fontSize: "12px",
        fontWeight: "bold",
      },
      improvement: {
        fontSize: "12px",
        fontWeight: "bold",
      },
      improvementPositive: {
        color: "#4caf50",
      },
      improvementNegative: {
        color: "#f44336",
      },
      chart: {
        display: "flex",
        alignItems: "flex-end",
        height: "100px",
        gap: "2px",
        background: "#222",
        borderRadius: "4px",
        padding: "5px",
      },
      point: {
        flex: 1,
        position: "relative",
        minWidth: "15px",
        borderRadius: "3px 3px 0 0",
      },
      pointValue: {
        position: "absolute",
        top: "-15px",
        left: "50%",
        transform: "translateX(-50%)",
        fontSize: "9px",
        color: "white",
      },
    };

    useEffect(() => {
      const fetchProgressionData = async () => {
        if (!clientId) return;
        setLoading(true);

        try {
          const progressData = {};

          // For each exposure, get all logs and track progress
          for (const exposure of exposures) {
            const logsRef = collection(
              db,
              "exposureHierarchy",
              clientId,
              "Logs"
            );
            const logQuery = query(
              logsRef,
              where("exposureTitle", "==", exposure.title),
              orderBy("timestamp", "asc")
            );

            const logSnap = await getDocs(logQuery);
            if (!logSnap.empty) {
              const logs = [];
              logSnap.forEach((doc) => {
                const data = doc.data();
                logs.push({
                  date: new Date(data.timestamp.seconds * 1000),
                  fearBelief: data.fearBelief ? Number(data.fearBelief) : null,
                  confidence: data.confidence ? Number(data.confidence) : null,
                });
              });

              progressData[exposure.title] = logs;
            }
          }

          setProgressionData(progressData);
        } catch (error) {
          console.error("Error fetching progression data:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchProgressionData();
    }, [exposures, clientId]);

    // Find exposures that have multiple logs for trend analysis
    const exposuresWithProgress = Object.keys(progressionData)
      .filter((title) => progressionData[title].length > 1)
      .map((title) => ({
        title,
        logs: progressionData[title],
        improvement: calculateImprovement(progressionData[title]),
      }))
      .sort((a, b) => b.improvement - a.improvement);

    // Helper function to calculate improvement percentage
    function calculateImprovement(logs) {
      if (logs.length < 2) return 0;

      const firstLog = logs[0];
      const lastLog = logs[logs.length - 1];

      if (!firstLog.fearBelief || !lastLog.fearBelief) return 0;

      // Make sure we're working with numbers
      const firstFear = Number(firstLog.fearBelief);
      const lastFear = Number(lastLog.fearBelief);

      // Calculate percentage reduction in fear
      const fearReduction = firstFear - lastFear;
      return (fearReduction / firstFear) * 100;
    }

    return (
      <div style={styles.tracker}>
        <h4>Exposure Progression</h4>
        <div style={styles.list}>
          {exposuresWithProgress.map((item) => (
            <div key={item.title} style={styles.item}>
              <div style={styles.header}>
                <span style={styles.title}>{item.title}</span>
                <span
                  style={{
                    ...styles.improvement,
                    ...(item.improvement >= 0
                      ? styles.improvementPositive
                      : styles.improvementNegative),
                  }}
                >
                  {item.improvement.toFixed(1)}%{" "}
                  {item.improvement >= 0 ? "↓" : "↑"}
                </span>
              </div>
              <div style={styles.chart}>
                {item.logs.map((log, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.point,
                      height: `${Number(log.fearBelief) * 10}%`,
                      backgroundColor: getFearColor(Number(log.fearBelief)),
                    }}
                    title={`${log.date.toLocaleDateString()}: Fear ${
                      log.fearBelief
                    }, Confidence ${log.confidence}`}
                  >
                    <span style={styles.pointValue}>{log.fearBelief}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  //*** 🖥️ Function to improve the vizualisation ***
  const ExposureHierarchyVisualization = ({ exposures }) => {
    // Filter out completed exposures and sort remaining by fear level (descending)
    const sortedExposures = [...exposures]
      .filter((exposure) => !exposure.completed)
      .sort((a, b) => b.fearBelief - a.fearBelief);

    // Define inline styles as JavaScript objects
    const styles = {
      container: {
        marginTop: "20px",
        marginBottom: "30px",
      },
      pyramid: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "2px",
      },
      step: {
        padding: "8px",
        borderRadius: "4px",
        color: "white",
        textAlign: "center",
        transition: "all 0.3s ease",
        minHeight: "22px",
      },
      stepContent: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      },
      stepFear: {
        fontWeight: "bold",
        minWidth: "25px",
      },
      stepTitle: {
        flexGrow: 1,
        textAlign: "center",
        fontSize: "12px",
      },
      stepConfidence: {
        fontSize: "10px",
        minWidth: "55px",
      },
      noExposures: {
        textAlign: "center",
        color: "#888",
        padding: "15px",
      },
    };

    // If no active exposures, show a message
    if (sortedExposures.length === 0) {
      return (
        <div style={styles.container}>
          <h4>Visual Hierarchy</h4>
          <div
            style={{
              ...styles.pyramid,
              backgroundColor: "#333",
              padding: "10px",
              borderRadius: "4px",
            }}
          >
            <p style={styles.noExposures}>No active exposures to display</p>
          </div>
        </div>
      );
    }

    return (
      <div style={styles.container}>
        <h4>Visual Hierarchy</h4>
        <div style={styles.pyramid}>
          {sortedExposures.map((exposure, index) => {
            // Calculate width percentage based on position in hierarchy
            const widthPercentage = 40 + (index / sortedExposures.length) * 60;

            // Calculate background color based on fear belief level
            const fearColor = exposure.fearBelief
              ? getFearColor(exposure.fearBelief)
              : "#fa6161";

            // Combine base step styles with dynamic styles
            const stepStyle = {
              ...styles.step,
              width: `${widthPercentage}%`,
              backgroundColor: fearColor,
            };

            return (
              <div
                key={exposure.id || index}
                style={stepStyle}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "scale(1.02)";
                  e.currentTarget.style.boxShadow =
                    "0 0 8px rgba(255, 255, 255, 0.2)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={styles.stepContent}>
                  <span style={styles.stepFear}>
                    {exposure.fearBelief || "?"}
                  </span>
                  <span style={styles.stepTitle}>{exposure.title}</span>
                  {exposure.confidence && (
                    <span style={styles.stepConfidence}>
                      Conf: {exposure.confidence}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  //*** 🖥️ Function to get today's date in a readable format ***
  const getFormattedDate = () => {
    const today = new Date();
    const options = { year: "numeric", month: "long", day: "numeric" };
    return today.toLocaleDateString(undefined, options); // Format the date
  };
  const dateString = getFormattedDate();

  const loadExposures = async (userId) => {
    setExposures([]);
    try {
      const docRef = doc(db, "exposureHierarchy", userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const exposuresData = docSnap.data().exposures || [];
        const exposuresWithLogs = await Promise.all(
          exposuresData.map(async (exposure) => {
            const logsRef = collection(db, "exposureHierarchy", userId, "Logs");
            const logQuery = query(
              logsRef,
              where("exposureTitle", "==", exposure.title), // Changed "title" to "exposureTitle"
              orderBy("timestamp", "desc"), // Ensure the logs are ordered by timestamp in descending order
              limit(1) // Only get the most recent log
            );
            const logSnap = await getDocs(logQuery);

            let confidence = null;
            let fearBelief = null;

            // Check if any logs were returned
            if (!logSnap.empty) {
              logSnap.forEach((logDoc) => {
                const logData = logDoc.data();
                confidence = logData.confidence || null; // Default to null if the field doesn't exist
                fearBelief = logData.fearBelief || null; // Default to null if the field doesn't exist
              });
            }

            return { ...exposure, confidence, fearBelief }; // Attach the last saved confidence and fearBelief to exposure
          })
        );

        setExposures(exposuresWithLogs); // Set exposures with the latest logs data
      }
    } catch (error) {
      console.error("Error loading exposures:", error);
    }
  };

  // Calculate progress
  const completedCount = exposures.filter(
    (exposure) => exposure.completed
  ).length;
  const totalCount = exposures.length;

  //*** 🖥️ Function to open modal when user clicks "Log Exposure" ***
  const openModal = (exposureTitle) => {
    setExposureData({
      exposurePlan: "",
      fearedOutcome: "",
      responsePrevention: "",
      fearBelief: 0,
      confidence: 0,
      exposureOutcome: "",
      exposureVariation: "",
    }); // Clear previous data when opening the modal for a new log
    setModalVisible(true);
    setCurrentExposureTitle(exposureTitle); // Store the title for later use in the log
  };

  //*** 🖥️ Function to close modal ***
  const closeModal = () => setModalVisible(false);

  // New state to hold the current exposure title for the modal

  //*** 🖥️ Load log history ***
  const [logHistory, setLogHistory] = useState([]); // State to store log history

  const loadLogHistory = async () => {
    const logsRef = collection(
      db,
      "exposureHierarchy",
      selectedClientId,
      "Logs"
    );
    const logQuery = query(
      logsRef,
      where("exposureTitle", "==", currentExposureTitle),
      orderBy("timestamp", "desc") // Sort by timestamp
    );

    try {
      const logSnap = await getDocs(logQuery);
      const logs = logSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLogHistory(logs); // Set the logs in state
    } catch (error) {
      console.error("Error loading log history:", error);
    }
  };

  //*** 🖥️ Call loadLogHistory when modal opens ***
  useEffect(() => {
    if (modalVisible) {
      loadLogHistory(); // Fetch the logs when modal is visible
    }
  }, [modalVisible, currentExposureTitle]); // Trigger on modal visibility and title change

  //*** 🖥️ Render the modal window ***
  // This is the modified modal for therapist view only
  const modal = (
    <div
      style={{
        ...modalStyles.overlay,
        overflow: "hidden", // Ensure the overlay doesn't scroll, only the content
      }}
    >
      <div
        style={{
          ...modalStyles.content,
          maxWidth: "700px",
          maxHeight: "90vh", // Limit height to 90% of viewport height
          overflowY: "auto", // Enable vertical scrolling
          overflowX: "hidden", // Prevent horizontal scrolling
          padding: "20px",
          position: "relative", // For proper scrolling
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#2e2e2e",
            paddingBottom: "10px",
            marginBottom: "10px",
            zIndex: 10,
          }}
        >
          <h2 style={{ color: "#f09a73", marginTop: "0px" }}>
            <span style={{ marginRight: "10px" }}>📝</span>
            Exposure Log History: {currentExposureTitle}
          </h2>
        </div>

        {/* Tabs for organization */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #444",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              padding: "8px 15px",
              backgroundColor: "#444",
              borderRadius: "5px 5px 0 0",
              fontWeight: "bold",
            }}
          >
            Exposure Details
          </div>
        </div>

        {/* History Display with visual cues */}
        <div style={{ marginBottom: "20px" }}>
          <h3
            style={{
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "8px" }}>📚</span>
            Patient's Log History
          </h3>
          <div
            style={{
              maxHeight: "120px",
              overflowY: "auto",
              backgroundColor: "#222",
              borderRadius: "8px",
              padding: "10px",
            }}
          >
            {logHistory.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {logHistory.map((log, index) => {
                  // Calculate color based on fear belief
                  const logColor = log.fearBelief
                    ? `hsl(${120 - log.fearBelief * 12}, 70%, 45%)`
                    : "#666";

                  return (
                    <div
                      key={log.id}
                      style={{
                        backgroundColor: "#333",
                        borderLeft: `4px solid ${logColor}`,
                        borderRadius: "4px",
                        padding: "8px 12px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        flex: "1 0 auto",
                        maxWidth: "calc(50% - 20px)",
                        minWidth: "200px",
                      }}
                      onClick={() => {
                        // Set the selected log for display (read-only)
                        setExposureData({
                          exposurePlan: log.exposurePlan || "",
                          fearedOutcome: log.fearedOutcome || "",
                          responsePrevention: log.responsePrevention || "",
                          fearBelief: log.fearBelief || "",
                          confidence: log.confidence || "",
                          exposureOutcome: log.exposureOutcome || "",
                          exposureVariation: log.exposureVariation || "",
                          timestamp: log.timestamp,
                        });
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#444";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#333";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                          fontSize: "11px",
                          color: "#aaa",
                        }}
                      >
                        <span>Log #{index + 1}</span>
                        <span>
                          {new Date(
                            log.timestamp.seconds * 1000
                          ).toLocaleDateString()}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "12px",
                        }}
                      >
                        <span style={{ color: "#f09a73" }}>
                          Fear: {log.fearBelief || "N/A"}
                        </span>
                        <span style={{ color: "#4caf50" }}>
                          Conf: {log.confidence || "N/A"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p
                style={{
                  textAlign: "center",
                  color: "#666",
                  margin: "10px 0",
                }}
              >
                No exposure logs found for this patient.
              </p>
            )}
          </div>
        </div>

        {/* Selected log details display (read-only) */}
        {Object.keys(exposureData).length > 0 && (
          <div className="log-details">
            <h3
              style={{
                fontSize: "14px",
                borderBottom: "1px solid #444",
                paddingBottom: "8px",
                marginBottom: "15px",
              }}
            >
              Log Details
              {exposureData.timestamp && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#aaa",
                    fontWeight: "normal",
                    marginLeft: "10px",
                  }}
                >
                  {new Date(
                    exposureData.timestamp.seconds * 1000
                  ).toLocaleString()}
                </span>
              )}
            </h3>

            <div style={{ marginBottom: "20px" }}>
              <h4
                style={{
                  fontSize: "13px",
                  margin: "0 0 5px 0",
                  color: "#4caf50",
                }}
              >
                <span style={{ marginRight: "5px" }}>📋</span>
                Exposure Plan:
              </h4>
              <div
                style={{
                  backgroundColor: "#333",
                  borderLeft: "4px solid #4caf50",
                  padding: "10px",
                  borderRadius: "4px",
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {exposureData.exposurePlan || "No plan recorded"}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <h4
                style={{
                  fontSize: "13px",
                  margin: "0 0 5px 0",
                  color: "#ff9800",
                }}
              >
                <span style={{ marginRight: "5px" }}>⚠️</span>
                Feared Outcome:
              </h4>
              <div
                style={{
                  backgroundColor: "#333",
                  borderLeft: "4px solid #ff9800",
                  padding: "10px",
                  borderRadius: "4px",
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {exposureData.fearedOutcome || "No feared outcome recorded"}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <h4
                style={{
                  fontSize: "13px",
                  margin: "0 0 5px 0",
                  color: "#f44336",
                }}
              >
                <span style={{ marginRight: "5px" }}>🚫</span>
                Response Prevention:
              </h4>
              <div
                style={{
                  backgroundColor: "#333",
                  borderLeft: "4px solid #f44336",
                  padding: "10px",
                  borderRadius: "4px",
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {exposureData.responsePrevention ||
                  "No response prevention recorded"}
              </div>
            </div>

            <div
              style={{
                marginBottom: "20px",
                display: "flex",
                gap: "20px",
              }}
            >
              <div style={{ flex: 1 }}>
                <h4
                  style={{
                    fontSize: "13px",
                    margin: "0 0 5px 0",
                    color: "#f09a73",
                  }}
                >
                  Fear Belief Rating:
                </h4>
                <div
                  style={{
                    backgroundColor: "#333",
                    borderLeft: "4px solid #f09a73",
                    padding: "10px",
                    borderRadius: "4px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  {exposureData.fearBelief || "N/A"} / 10
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <h4
                  style={{
                    fontSize: "13px",
                    margin: "0 0 5px 0",
                    color: "#4caf50",
                  }}
                >
                  Confidence Rating:
                </h4>
                <div
                  style={{
                    backgroundColor: "#333",
                    borderLeft: "4px solid #4caf50",
                    padding: "10px",
                    borderRadius: "4px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  {exposureData.confidence || "N/A"} / 10
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <h4
                style={{
                  fontSize: "13px",
                  margin: "0 0 5px 0",
                  color: "#2196f3",
                }}
              >
                <span style={{ marginRight: "5px" }}>🔍</span>
                Exposure Outcome:
              </h4>
              <div
                style={{
                  backgroundColor: "#333",
                  borderLeft: "4px solid #2196f3",
                  padding: "10px",
                  borderRadius: "4px",
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {exposureData.exposureOutcome || "No outcome recorded"}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <h4
                style={{
                  fontSize: "13px",
                  margin: "0 0 5px 0",
                  color: "#9c27b0",
                }}
              >
                <span style={{ marginRight: "5px" }}>🔄</span>
                Future Variations:
              </h4>
              <div
                style={{
                  backgroundColor: "#333",
                  borderLeft: "4px solid #9c27b0",
                  padding: "10px",
                  borderRadius: "4px",
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {exposureData.exposureVariation || "No variations recorded"}
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            ...modalStyles.buttonContainer,
            position: "sticky",
            bottom: 0,
            backgroundColor: "#2e2e2e",
            paddingTop: "15px",
            marginTop: "15px",
            borderTop: "1px solid #444",
          }}
        >
          <button
            type="button"
            onClick={closeModal}
            style={{ ...modalStyles.button, ...modalStyles.cancelButton }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  //************************** 📜 Mood Tracker **************************
  const statusBar1Ref = useRef(null);
  const statusBar2Ref = useRef(null);
  const statusBar3Ref = useRef(null);
  const statusBar4Ref = useRef(null);
  const [moodData, setMoodData] = useState({});
  const [emojiPercentages, setEmojiPercentages] = useState({
    1: 0, // For 😊
    2: 0, // For 🙂
    3: 0, // For 😟
    4: 0, // For 😫
  });
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const moodEmojis = {
    1: "😊",
    2: "🙂",
    3: "😟",
    4: "😫",
  };

  // *** 🖥️ Fetch mood data from Firestore ***
  const fetchMoodData = async (userId) => {
    setMoodData({});
    setEmojiPercentages({
      1: 0, // For 😊
      2: 0, // For 🙂
      3: 0, // For 😟
      4: 0, // For 😫
    });

    if (userId) {
      const docRef = doc(db, "moodTracker", userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (Object.keys(data).length > 0) {
          setMoodData(data);
          updateEmojiPercentages(data);
        }
      }
      //setLoading(false);
    }
  };

  // *** 🖥️ Update the emoji percentages ***
  const updateEmojiPercentages = (updatedMoodData) => {
    const emojiCounts = Object.values(updatedMoodData).reduce(
      (counts, mood) => {
        counts[mood] = (counts[mood] || 0) + 1;
        return counts;
      },
      {}
    );
    const totalEntries = Object.values(updatedMoodData).filter(
      (value) => value !== null
    ).length;
    const newEmojiPercentages = {};
    for (const mood in moodEmojis) {
      const count = emojiCounts[mood] || 0;
      newEmojiPercentages[mood] =
        totalEntries > 0 ? ((count / totalEntries) * 100).toFixed(0) : 0;
    }
    setEmojiPercentages(newEmojiPercentages);
  };

  // *** 🖥️ Render the grid with days and months ***
  const today = new Date();
  const todayKey = `${today.getMonth()}-${today.getDate()}`;
  const getDaysInMonth = (monthIndex, year) => {
    const date = new Date(year, monthIndex + 1, 0);
    return date.getDate();
  };
  const renderGrid = () => {
    const currentYear = new Date().getFullYear();
    return months.map((month, monthIndex) => {
      const daysInMonth = getDaysInMonth(monthIndex, currentYear);
      return (
        <div key={month} className="month-row">
          <div className="month-label">{month}</div>
          <div className="days-row">
            {Array.from({ length: daysInMonth }, (_, day) => {
              const key = `${monthIndex}-${day + 1}`;
              const isToday = key === todayKey;
              return (
                <div
                  key={day + 1}
                  className={`grid-cell mood-${moodData[key] || 0} ${
                    isToday ? "today-square" : ""
                  }`}
                >
                  {moodEmojis[moodData[key]] || ""}
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  // *** 🖥️ Function to generate status bars dynamically ***
  const generateStatusBar = (ref, percentage) => {
    if (ref.current) {
      const statusBar = ref.current;
      statusBar.innerHTML = "";
      const statusLabel = statusBar.previousElementSibling;
      statusLabel.textContent = `${percentage}%`;

      const filledCount = Math.round((percentage / 100) * 10);
      const colors = [];
      for (let i = 0; i < 10; i++) {
        const lightness = 60 - i * 6;
        const color = `hsl(120, 60%, ${lightness}%)`;
        colors.push(color);
      }

      for (let i = 0; i < filledCount; i++) {
        const symbol = document.createElement("span");
        symbol.classList.add("status-symbol");
        symbol.style.backgroundColor = colors[i];
        statusBar.appendChild(symbol);
      }

      for (let i = filledCount; i < 10; i++) {
        const symbol = document.createElement("span");
        symbol.classList.add("status-symbol", "empty");
        statusBar.appendChild(symbol);
      }
    }
  };

  useEffect(() => {
    if (statusBar1Ref.current && emojiPercentages[1] !== undefined) {
      generateStatusBar(statusBar1Ref, emojiPercentages[1] || 0);
    }
    if (statusBar2Ref.current && emojiPercentages[2] !== undefined) {
      generateStatusBar(statusBar2Ref, emojiPercentages[2] || 0);
    }
    if (statusBar3Ref.current && emojiPercentages[3] !== undefined) {
      generateStatusBar(statusBar3Ref, emojiPercentages[3] || 0);
    }
    if (statusBar4Ref.current && emojiPercentages[4] !== undefined) {
      generateStatusBar(statusBar4Ref, emojiPercentages[4] || 0);
    }
  }, [emojiPercentages]);

  //******************* 📜 OCD Goals *******************
  const [goals, setGoals] = useState({
    workingLife: "",
    homeLife: "",
    socialLife: "",
    relationships: "",
    personalLeisureTime: "",
  });

  // *** 🖥️ Fetch goals from Firestore ***
  const fetchGoals = async (userId) => {
    setGoals({
      workingLife: "",
      homeLife: "",
      socialLife: "",
      relationships: "",
      personalLeisureTime: "",
    });
    const docRef = doc(db, "ocdGoals", userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      setGoals(docSnap.data());
    }
  };

  //******************* 📜 Trigger Tracker *******************
  const [data, setData] = useState([]);
  const distressTriggerColors = {
    "😄": "#61fa69", // Green for lowest distress
    "🙂": "#8afa53",
    "😐": "#aefa3c",
    "😟": "#e89c2a",
    "😢": "#fa6161", // Red for highest distress
  };

  // *** 🖥️ Fetch trigger data from Firebase Firestore ***
  const loadTriggers = async (userId) => {
    setData([]);
    try {
      const docRef = doc(db, "ocdTriggers", userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setData(docSnap.data().triggers || []);
      }
    } catch (error) {
      console.error("Error loading triggers:", error);
    }
  };

  //******************* 📜 SUDS Tracker *******************
  const [allHistoricalData, setAllHistoricalData] = useState([]);

  //*** 🖥️ Load all historical data from Firestore ***
  const loadAllHistoricalDataFromFirestore = async (userId) => {
    setAllHistoricalData([]);
    try {
      const docRef = doc(db, "ocdSuds", userId); // Reference to Firestore document
      const docSnap = await getDoc(docRef); // Get the document snapshot

      if (docSnap.exists()) {
        const data = docSnap.data();
        const formattedData = Object.keys(data).map((trigger) => ({
          trigger,
          data: splitDataBySession(data[trigger]), // Split data by session
        }));
        setAllHistoricalData(formattedData);
      } else {
        console.error("No SUDS data found for this user.");
      }
    } catch (error) {
      console.error("Error fetching data from Firestore:", error);
    }
  };

  //*** 🖥️ Function to split data by session whenever time = 0 ***
  const splitDataBySession = (data) => {
    const sessions = [];
    let currentSession = [];

    data.forEach((entry) => {
      if (entry.time === 0 && currentSession.length > 0) {
        sessions.push(currentSession);
        currentSession = [];
      }
      currentSession.push(entry);
    });

    if (currentSession.length > 0) {
      sessions.push(currentSession); // Push the last session if it exists
    }

    return sessions;
  };

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    let fill = "lightblue";
    let r = 2;

    if (payload.level >= 80) {
      fill = "red";
      r = 4;
    } else if (payload.level <= 20) {
      fill = "green";
      r = 4;
    }

    return <Dot cx={cx} cy={cy} r={r} fill={fill} />;
  };

  const lineColors = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#8dd1e1", // Existing colors
    "#d45087",
    "#f95d6a",
    "#ff7c43",
    "#a05195",
    "#665191",
    "#2f4b7c",
    "#4caf50",
    "#ff5722",
    "#607d8b",
    "#795548",
    "#9c27b0",
    "#673ab7",
    "#2196f3",
    "#3f51b5",
  ];

  // ****** Email Notification *****

  // Simplified version without timestamp comparison in the query
  // Add this function to check for unread messages
  const checkForUnreadMessages = async () => {
    if (!auth.currentUser) return;

    try {
      const newUnreadMessages = {};

      // For each client, check if there are any unread messages
      for (const client of clients) {
        if (client.id === "ADjKRnX1iXduMuFV3GRmpjnAk7m1") continue; // Skip demo account

        // Look for the client's last read message timestamp in Firestore
        const lastReadRef = doc(db, "lastReadMessages", auth.currentUser.uid);
        const lastReadDoc = await getDoc(lastReadRef);

        let lastReadTime = new Date(0); // Default to epoch start

        if (lastReadDoc.exists() && lastReadDoc.data()[client.id]) {
          lastReadTime = new Date(lastReadDoc.data()[client.id].seconds * 1000);
        }

        // Get messages from this client
        const messagesRef = collection(db, "messages");
        const q = query(
          messagesRef,
          where("senderId", "==", client.id),
          where("receiverId", "==", auth.currentUser.uid),
          where("messageType", "==", "patient"),
          orderBy("timestamp", "desc")
        );

        try {
          const querySnapshot = await getDocs(q);
          let unreadCount = 0;

          querySnapshot.forEach((doc) => {
            const messageData = doc.data();
            const messageTime =
              messageData.timestamp instanceof Date
                ? messageData.timestamp
                : new Date(messageData.timestamp.seconds * 1000);

            if (messageTime > lastReadTime) {
              unreadCount++;
            }
          });

          if (unreadCount > 0) {
            newUnreadMessages[client.id] = unreadCount;
          }
        } catch (error) {
          console.error(
            `Error fetching messages for client ${client.id}:`,
            error
          );
        }
      }

      setUnreadMessages(newUnreadMessages);
    } catch (error) {
      console.error("Error checking for unread messages:", error);
    }
  };

  // Update the markMessagesAsRead function to update the timestamp in Firestore
  const markMessagesAsRead = async (clientId) => {
    if (!auth.currentUser) return;

    try {
      // Update the lastReadMessages document for this therapist and client
      const lastReadRef = doc(db, "lastReadMessages", auth.currentUser.uid);
      await setDoc(
        lastReadRef,
        {
          [clientId]: serverTimestamp(),
        },
        { merge: true }
      );

      // Update local state
      setUnreadMessages((prev) => ({
        ...prev,
        [clientId]: 0,
      }));
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  // Call checkForUnreadMessages when clients are loaded and auth is ready
  useEffect(() => {
    if (clients.length > 0 && auth.currentUser) {
      checkForUnreadMessages();
    }
  }, [clients, auth.currentUser]);

  const displayStat = async (myclient) => {
    setnoClient(false);
    setSelectedClientId(myclient);
    fetchCoupons(myclient);
    loadJournalEntries(myclient);
    fetchAssessments(myclient);
    loadExposures(myclient);
    loadTriggers(myclient);
    fetchGoals(myclient);
    fetchMoodData(myclient);
    loadAllHistoricalDataFromFirestore(myclient);
    fetchMessages(myclient);

    markMessagesAsRead(myclient);
  };

  // *****************************************************************
  // ************************** RETURN *******************************
  // *****************************************************************
  return (
    <>
      <style>
        {`
					/* TherapistDashboard.css */

					body {
						background-color: #3e3e3e;
					}

					.therapist-dashboard {
						max-width: 1200px;
						margin: 20px auto;
						margin-top: 20px;
						padding: 20px;
						background-color: #1e1e1e;
						color: white;
						border-radius: 8px;
						box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);

					}
					
					.therapist-dashboard p {
							font-size: 12px;
						}


					.blinking-red {
						color: #f09a73;
						font-weight: bold;
					}

					/* Style for the select dropdown */
					.select1-dropdown {
						display: block;
						width: 100%;
						padding: 10px;
						font-size: 12px;
						color: white;
						background-color: #4caf50;
						border: none;
						border-radius: 5px;
						appearance: none; /* Removes default styling in some browsers */
						-webkit-appearance: none; /* For Safari */
						-moz-appearance: none; /* For Firefox */
						cursor: pointer;
						transition: all 0.3s ease;
					}

					.select1-dropdown:focus {
						border-color: #007bff;
						outline: none;
						box-shadow: 0 0 5px rgba(0, 123, 255, 0.3);
					}

					.select1-dropdown option {
						padding: 10px;
						
					}

					/* Add some spacing between the dropdown and other elements */
					.select1-container {
						position: relative;
						width: 138px;
					}


					.therapist-dashboard .error {
						color: #d9534f;
						margin-top: 10px;
					}

					.therapist-dashboard .success {
						color: #4caf50;
						margin-top: 10px;
					}

					.modal-exposure-overlay {
						position: fixed;
						top: 0;
						left: 0;
						right: 0;
						bottom: 0;

						display: flex;
						justify-content: center;
						align-items: center;
					}

					.modal-exposure-content {
						background-color: #2e2e2e;
						padding: 20px;
						width: 90%;
						max-width: 800px;
						border-radius: 10px;
						box-shadow: 1 4px 8px rgba(255, 255, 255, 0.1);
					}

					.modal-exposure-content h2 {
						font-size: 1.5rem;
						margin-bottom: 15px;
						color: #f09a73;
						text-align: left;
					}

					/* Input and button on the same line */
					.input-exposure-group {
						display: flex;
						align-items: center;
						justify-content: space-between;
						margin-bottom: 20px; /* Space between input and actions */
					}

					.input-exposure-group input {
						width: 75%; /* Make the input field wider */
						padding: 10px;
						margin-right: 10px; /* Space between input and button */
						border-radius: 4px;
						border: 1px solid #ccc;
					}

					.modal-exposure-actions {
						margin-top: 20px;
						display: flex;
						justify-content: space-between;
					}

					/* General container for feature votes */

					.feature-container {
						
						margin: 0 0;
						padding: 20px;

					}
					/* Flexbox container for the features table */
					.feature-table {
						display: flex;
						flex-direction: column;
						margin-top: 10px;
					}

					/* Each feature row */
					.feature-row {
						display: flex;
						justify-content: space-between;
						align-items: left;
						background-color: #2e2e2e;
						border-radius: 10px;
						padding: 10px;
						font-size: 12px;
					}

					.feature1-row {
						display: flex;
						justify-content: space-between;
						align-items: left;
						background-color: #222222;
						border-radius: 10px;
						padding: 10px;
					}

					/* Style for each cell in the row */
					.feature-cell {
						flex: 0 0 auto;
						padding: 5px;
						text-align: left;
					}

					/* First column (Title) takes 20% */

					.feature-cell:nth-child(1) {
						width: 25%;
					}

					/* Second column (Description) takes 40% */

					.feature-cell:nth-child(2) {
						width: 50%;
					}

					.feature-cell:nth-child(3) {
						width: 10%;
					}
					.feature-cell:nth-child(4) {
						width: 15%;
					}

					/* Button styling */
					.feature-cell button {
						background-color: #4CAF50;
						color: white;
						border: none;
						padding: 10px 20px;
						border-radius: 5px;
						cursor: pointer;
						transition: background-color 0.3s ease;
					}

					.feature-cell button:hover {
						background-color: #45a049;
					}

					/* Vote count styling */
					.feature-cell:last-child {
						font-weight: bold;
						color: white;
					}

					.feature1-cell {
						flex: 0 0 auto;
						padding: 5px;
						padding-top: 1px;
						padding-bottom: 1px;
						text-align: left;
					}

					/* First column (Title) takes 20% */

					.feature1-cell:nth-child(1) {
						width: 15%;
					}

					/* Second column (Description) takes 40% */

					.feature1-cell:nth-child(2) {
						width: 25%;
					}

					.feature1-cell:nth-child(3) {
						width: 43%;
					}
					.feature1-cell:nth-child(4) {
						width: 17%;
					}
					
					.link-btn {
							padding: 8px 12px;
							margin: 5px 0;
							border: none;
							background-color: #4caf50;
							color: white;
							font-size: 14px;
							border-radius: 4px;
							cursor: pointer;
							transition: background-color 0.2s ease;
						}

						.link-btn:hover {
							background-color: #45a049;

						}
						
						.loading-spinner {
							border: 8px solid #f3f3f3;
							border-top: 8px solid #3498db;
							border-radius: 50%;
							width: 60px;
							height: 60px;
							animation: spin 1s linear infinite;
							margin: 20px auto;
						}
						
						.loading-container {
							display: flex;
							justify-content: center;
							align-items: center;
							height: 100vh;
						}
						
						.dashboard-container {
							display: grid;
							grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
							grid-gap: 20px;
							padding: 20px;
						}
						
						.dashboard-title {
							align-items: center;
							font-size: 1.2em;
							font-weight: bold;
							color: white;
						}
						
							.tile {
							position: relative;
							background-color: #222222;
							color: white;
							border-radius: 8px;
							padding: 20px;
							display: flex;
							flex-direction: column;
							justify-content: space-between;
							box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
							overflow: hidden;
						}

						.tile::before,
						.tile::after {
							content: '';
							position: absolute;
							top: 0;
							left: 0;
							right: 0;
							bottom: 0;
							border-radius: 8px;
						}

						.tile::before {
							border: 4px double #333;
							z-index: -1;
						}

						.tile::after {
							border: 2px solid #444;
							z-index: -2;
						}

						.tile .nested-tile {
							position: relative;
							background-color: #2b2b2b;
							/* Darker background for the nested tile */
							color: #fff;
							border-radius: 8px;
							padding: 15px;
							box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
							/* Shadow for nested tile */
						}

						.tile .nested-tile::before,
						.tile .nested-tile::after {
							content: '';
							position: absolute;
							top: 0;
							left: 0;
							right: 0;
							bottom: 0;
							border-radius: 8px;
						}

						.tile .nested-tile::before {
							border: 4px double #444;
							/* Outer double border effect for nested tile */
							z-index: -1;
							/* Keep the border behind the tile content */
						}

						.tile .nested-tile::after {
							border: 2px solid #555;
							/* Inner border for nested tile */
							z-index: -2;
							/* Keep this behind the other borders */
						}

						/* Example: Set size for specific tiles */
						.tile-1 {
							grid-column: span 2;
							/* Span across 2 columns */
							grid-row: span 2;
							/* Span across 2 rows */
						}
						
						.tile-2 {
							grid-column: span 1;
							/* Standard width */
							grid-row: span 1;
							/* Standard height */
						}
						
						.tile-header {
							display: flex;
							align-items: center;
							font-size: 1.2em;
							font-weight: bold;
						}

						.tile-header .icon {
							font-size: 1em;
						}


						.tile-content {
							flex-grow: 1;
						}


						.tile-content p {
							font-size: 12px;
						}


						.tile .icon {
							width: 20px;
							height: 20px;
							margin-right: 10px;
						}
						
						.event-link {
							color: #007bff;
							text-decoration: none;
							font-size: 10px;
						}

						.event-link:hover {
							text-decoration: underline;
						}
						.coupon-history1 {
							table-layout: fixed;
							border-collapse: collapse;

							width: 100%;
						}

						.coupon-history1 th,
						.coupon-history1 td {
							border: 0px solid #ccc;
							padding: 0px;

							word-wrap: break-word;
						}

						.coupon-history1 th {
							background-color: #575656;
							font-size: 12px;
							text-align: center;
						}

						.coupon-history1 td {
							line-height: 0.8rem;
							font-size: 12px;
							text-align: left;
							padding-right: 3px;
						}
						
						/* Entries List */
						.entries-list {
							margin-top: 10px;
							font-size: 12px;
							align-items: top;

						}
						
							.entries-list th {
							background-color: #575656;
							text-align: center;
						}

						.entries-list td {
							line-height: 0.8rem;
							font-size: 10px;
							text-align: left;
							padding-right: 3px;
						}

						.entries-list li span {
							font-weight: normal;
							color: #777;
						}
						
						.exposure ul {
							list-style-type: none;
							margin-left: 5px;
							padding: 5px;
							border: 0px solid white;
							background-color: #333;
							border-radius: 4px;
						}

						.exposure li {
							display: flex;
							align-items: center;
							justify-content: right;
							gap: 5px;
							padding: 0px;
							font-size: 12px;
							margin-bottom: 0px;

						}

						.exposure-btn {
							padding: 3px 3px;
							margin: 1px;
							border: none;
							background-color: #4caf50;
							color: white;
							font-size: 12px;
							border-radius: 4px;
							cursor: pointer;
							transition: background-color 0.2s ease;
						}

						.exposure-btn:hover {
							background-color: #45a049;

						}
						
						.tracker-table {
							table-layout: fixed;
							border-collapse: collapse;

							width: 100%;
						}

						.tracker-table th,
						.tracker-table td {
							border: 0px solid #ccc;
							padding: 0px;

							word-wrap: break-word;
						}

						.tracker-table th {
							background-color: #575656;
							font-size: 12px;
							text-align: center;
						}

						.tracker-table td {
							line-height: 0.8rem;
							font-size: 10px;
							text-align: left;
							padding-right: 3px;
						}

						.goal-content textarea {

							width: 95%;
							height: 70px;
							margin-bottom: 10px;
							border-radius: 5px;
							font-size: 12px;
						}
					
											.status-row {
							display: flex;
							align-items: right;
							margin-bottom: 0px;
							margin-left: 60%;
							padding: 0;
							line-height: 1;
							color: white;
							font-size: 12px;
							justify-content: space-between;
							width: 200px;
						}

						.status-row .icon {

							font-size: 14px;
						}

						.status-bar {
							display: inline-block;
							width: 130px;
							height: 20px;

						}

						.status-symbol {
							display: inline-block;
							width: 10%;
							height: 100%;
							background-color: #e0e0e0;
						}

						.status-bar .status-symbol {
							margin-right: 2px;
							/* Space between blocks */
							width: 10px;
							/* Size of each block */
							height: 10px;
							/* Size of each block */
							text-align: center;
							color: white;
							/* Default color for text */
							font-weight: bold;
							display: inline-block;
						}

						.empty {
							background-color: #343635;
						}

						.emoji-percentages {
							display: flex;
							justify-content: space-around;
							flex-wrap: wrap;
							/* Allow wrapping for smaller screens */
							padding: 10px;
							margin: auto;
							margin-bottom: 20px;
							border-radius: 8px;
							font-size: 1.0em;
							box-shadow: 0 20px 20px rgba(0, 0, 0, 0.1);
							max-width: 700px;
							/* Center within max width */
							background-color: #1e1e1e;
						}

						.emoji-percentages div {
							display: flex;
							align-items: center;
							gap: 20px;
							/* Default gap */
							margin: 5px 10px;
							/* Margin for wrapped items */
							font-weight: bold;
							color: hsl(0, 0%, 100%);
						}

						.emoji-percentages div::before {
							content: attr(data-emoji);
							font-size: 1.0em;
						}

						.emoji-percentages {
							font-size: 0.5rem;
							/* Slightly smaller font size */
						}

						.emoji-percentages div {
							gap: 5px;
							/* Reduce gap on smaller screens */
						}

						.container1-mood {
							margin: auto;
							/* Fills the viewport height */
							font-size: 0.8em;
						}

						.calendar-grid {
							margin-top: 25px;
							margin-bottom: 20px;
							display: flex;
							flex-direction: column;
							line-height: normal
						}

						.month-row {
							display: flex;
							align-items: center;
							margin-bottom: 5px;
						}

						.month-label {
							width: 50px;
							text-align: right;
							margin-right: 10px;
							font-size: 12px;
						}

						.days-row {
							display: grid;
							grid-template-columns: repeat(31, 15px);
							/* 31 columns for days */
							gap: 1px;
						}

						.grid-cell {
							width: 15px;
							height: 15px;
							background-color: #333;
							border-radius: 3px;
							cursor: pointer;
						}

						.grid-cell.mood-1 {
							background-color: #4caf50;
							/* Happy */
						}

						.grid-cell.mood-2 {
							background-color: #ffeb3b;
							/* Neutral */
						}

						.grid-cell.mood-3 {
							background-color: #4b30e6;
						}

						.grid-cell.mood-4 {
							background-color: #f44336;
							/* Sad */
						}

						/* Highlight today's square */
						.today-square {
							border: 2px solid #ff9800;
							/* Bright orange border */
							animation: pulse 1s infinite;
							/* Optional subtle animation */
						}

						/* Optional pulse animation */
						@keyframes pulse {
							0% {
								box-shadow: 0 0 5px #ff9800;
							}

							50% {
								box-shadow: 0 0 15px #ff9800;
							}

							100% {
								box-shadow: 0 0 5px #ff9800;
							}
						}
						
						/* Message Board */
						.message-board {
							display: flex;
							flex-direction: column;
						}

						/* Messages List */
						.messages-list {
							max-height: 400px;
							overflow-y: scroll;
							margin-bottom: 10px;
							border: 0px solid #ddd;
							border-radius: 8px;
							padding: 10px;
							background-color: #333;
							font-size: 12px;
						}

						/* Individual Message Styles */
						.message {
							padding: 10px;
							margin-bottom: 10px;
							border-radius: 8px;


						}

						.message.patient {
							background-color: #1e1e1e;
							color: #4caf50;
							font-size: 12px;
						}

						.message.therapist {
							background-color: #1e1e1e;
							color: #007bff;
							font-size: 12px;
							text-align: right;
						}

						.message.admin {
							background-color: #faf261;
							color: black;
							font-size: 12px;
							text-align: right;
						}

						.message p {
							margin: 0;
							font-size: 12px;
						}

						.message span {
							font-size: 10px;
							color: white;
						}

						/* Send Message Area */
						.send-message {
							display: flex;
							flex-direction: column;
						}

						.send-message textarea {
							width: 90%;
							height: 50px;
							margin-bottom: 10px;

							padding: 10px;
							border-radius: 8px;
							border: 1px solid #ccc;
							font-size: 12px;
							resize: none;
						}

						.send-message button:disabled {
							background-color: #aaa;
							cursor: not-allowed;
						}
						
						@keyframes pulse {
							0% { opacity: 1; }
							50% { opacity: 0.5; }
							100% { opacity: 1; }
						  }

					
					`}
      </style>
      <Navbar />
      <div className="therapist-dashboard">
        <h2 className="dashboard-title">
          ↔️ How To Access Your Patients' Dashboard
        </h2>
        <p>
          ✅ Share your "Therapist Secret Key" (
          <span className="blinking-red">{therapistSecretKey}</span>) with your
          patient.
          <br />
          ✅ Ask him/her to register on the website and enter your “Therapist
          Secret Key” in the “Therapist Access” tile on their dashboard.
          <br />
        </p>
        <br />
        {/* Client List */}
        <section>
          <h2 className="dashboard-title">👥 Your Patients</h2>

          {loading ? (
            <p>Loading patients...</p>
          ) : error ? (
            <p className="error">{error}</p>
          ) : clients.length > 0 ? (
            <>
              <div>
                <div className="feature-container">
                  <div className="feature1-row">
                    <div className="feature1-cell">
                      <strong>Alias</strong>
                    </div>
                    <div
                      className="feature1-cell"
                      style={{ width: "100px", textAlign: "center" }}
                    >
                      <b>Y-BOCS</b>
                    </div>

                    <div className="feature1-cell">
                      <b>Comment</b>
                    </div>
                    <div className="feature1-cell"></div>
                  </div>

                  {clients.map((client) => (
                    <div key={client.id} className="feature-table">
                      <div className="feature-row">
                        <div className="feature1-cell">
                          <strong>{client.alias}</strong>
                          {unreadMessages[client.id] > 0 && (
                            <span
                              className="blinking-envelope"
                              style={{
                                marginLeft: "10px",
                                animation: "blink 1s infinite",
                              }}
                            >
                              ✉️
                            </span>
                          )}
                        </div>
                        <div
                          className="feature1-cell"
                          style={{ width: "100px", textAlign: "center" }}
                        >
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "bold",
                              color:
                                clientScores[client.id] &&
                                clientScores[client.id] !== "N/A"
                                  ? clientScores[client.id] >= 24
                                    ? "#f44336"
                                    : clientScores[client.id] >= 16
                                    ? "#ff9800"
                                    : "#4caf50"
                                  : "#aaa",
                            }}
                          >
                            {clientScores[client.id] || "N/A"}
                          </span>
                        </div>
                        <div
                          className="feature1-cell"
                          style={{ width: "650px" }}
                        >
                          {client.comment}
                        </div>
                        <div
                          className="feature1-cell"
                          style={{ width: "100px" }}
                        >
                          <button
                            onClick={() => openEditModal(client)}
                            className="link-btn"
                          >
                            Edit Profile
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p>No clients added yet.</p>
          )}
        </section>

        {/* Edit Client Modal */}
        {isEditing && selectedClient && (
          <EditClientForm
            client={selectedClient}
            onClose={closeEditModal}
            onUpdate={fetchClients}
          />
        )}

        {/* Message Client Modal */}
        {isMessaging && selectedClient && (
          <MessageModal
            client={selectedClient}
            therapistId={auth.currentUser?.uid}
            onClose={closeMessagingModal}
          />
        )}
      </div>
      <div className="therapist-dashboard">
        <h2 className="dashboard-title">📊 Patients' Personal Dashboard</h2>
        <p>Select a patient:</p>
        <div>
          {clients.map((client) => (
            <button
              key={client.id}
              className="link-btn"
              style={{ marginLeft: "5px" }}
              onClick={() => displayStat(client.id)}
            >
              {client.alias}
            </button>
          ))}
        </div>
        <br />

        {noClient ? (
          <p>No patient selected.</p>
        ) : (
          <>
            <div className="dashboard-container">
              <div class="tile tile-2">
                <div class="tile-header">
                  <span class="icon">🎟️</span>Reassurance Coupon
                </div>
                <div class="tile-content">
                  <p style={{ textAlign: "center" }}>
                    ➡️{" "}
                    <a
                      href="https://www.ocdserenity.com/Reassurance-coupons.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-link"
                    >
                      Download printable coupons here
                    </a>{" "}
                    ⬅️
                  </p>
                  <div className="coupon-history1">
                    <h4 style={{ marginTop: "5px", marginBottom: "5px" }}>
                      Coupon History
                    </h4>
                    {history.length > 0 ? (
                      <>
                        <table className="history-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Coupons Given</th>
                            </tr>
                          </thead>
                          <tbody>
                            {history.map((entry, index) => (
                              <tr key={index}>
                                <td>{entry.timestamp}</td>
                                <td>
                                  {entry.coupons} (
                                  {Array.from(entry.coupons).length / 2})
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <p style={{ textAlign: "center", color: "#4caf50" }}>
                        No history available.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="tile tile-1" style={{ maxHeight: "800px" }}>
                <div className="tile-header">
                  <span className="icon">⏱</span>Journaling
                </div>
                <div className="tile-content">
                  <h4>Previous Entries</h4>
                  <div
                    className="entries-list"
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr
                          style={{
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                          }}
                        >
                          <th style={{ width: "50px" }}>Date</th>
                          <th style={{ width: "50px" }}>Mood</th>
                          <th>Entry</th>
                        </tr>
                      </thead>
                      <tbody>
                        {journalEntries.map((entry, index) => {
                          // Define colors for each emoji value
                          const emojiColors = {
                            Happy: "#61fa69",
                            "Slightly happy": "#faf261",
                            Worried: "#fa9961",
                            "Very distressed": "#fa6161",
                          };

                          // Get the color corresponding to the emoji
                          const textColor = emojiColors[entry.emoji] || "black"; // Default to black if emoji doesn't match

                          return (
                            <tr key={index}>
                              <td
                                style={{
                                  backgroundColor: textColor,
                                  color: "black",
                                  paddingLeft: "5px",
                                }}
                              >
                                {entry.date}
                              </td>
                              <td
                                style={{
                                  backgroundColor: textColor,
                                  color: "black",
                                }}
                              >
                                {entry.emoji}
                              </td>
                              <td
                                style={{
                                  backgroundColor: textColor,
                                  color: "black",
                                }}
                              >
                                {entry.text}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div class="tile tile-2" style={{ height: "190px" }}>
                <div class="tile-header">
                  <span class="icon">📝</span>Assessment Score
                </div>
                <div class="tile-content">
                  <p
                    style={{
                      display: "flex",
                      justifyContent: "space-between",

                      alignItems: "center",
                    }}
                  >
                    <b>
                      Y-BOCS:
                      <span style={{ fontSize: "18px", color: "#4caf50" }}>
                        &nbsp;{scores.totalScore || "N/A"}
                      </span>
                    </b>
                    <button
                      onClick={() =>
                        navigate(`/ybocs-statistics`, {
                          state: { clientId: "nptDT1Ngd2enpd3yD7V8YNFVBuJ3" },
                        })
                      }
                      className="link-btn"
                    >
                      Y-BOCS History
                    </button>
                  </p>
                  <p
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <b>
                      PHQ-9:
                      <span style={{ fontSize: "18px", color: "#4caf50" }}>
                        &nbsp;{scoresPHQ9.totalScore || "N/A"}
                      </span>
                    </b>
                    <button
                      onClick={() =>
                        navigate(`/phq9-statistics`, {
                          state: { clientId: "nptDT1Ngd2enpd3yD7V8YNFVBuJ3" },
                        })
                      }
                      className="link-btn"
                    >
                      PHQ-9 History
                    </button>
                  </p>
                  <p
                    style={{
                      display: "flex",
                      justifyContent: "space-between",

                      alignItems: "center",
                    }}
                  >
                    <b>
                      GAD-7:
                      <span style={{ fontSize: "18px", color: "#4caf50" }}>
                        &nbsp;{scoresGAD7.totalScore || "N/A"}
                      </span>
                    </b>
                    <button
                      onClick={() =>
                        navigate(`/gad7-statistics`, {
                          state: { clientId: "nptDT1Ngd2enpd3yD7V8YNFVBuJ3" },
                        })
                      }
                      className="link-btn"
                    >
                      GAD-7 History
                    </button>
                  </p>
                </div>
              </div>

              <div class="tile tile-1">
                <div class="tile-header">
                  <span class="icon">🚀</span>Exposure Hierarchy Builder
                </div>
                <div class="tile-content">
                  {/* Progress bar */}

                  <div style={{ display: "flex", alignItems: "center" }}>
                    <h4>Progress:&nbsp;</h4>
                    <div
                      style={{
                        width: "65%",
                        backgroundColor: "#ddd",
                        height: "20px",
                        marginRight: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: `${(completedCount / totalCount) * 100}%`,
                          height: "100%",
                          backgroundColor: "#4caf50",
                        }}
                      ></div>
                    </div>
                    <span style={{ fontSize: "12px" }}>
                      {completedCount}/{totalCount} completed
                    </span>
                  </div>

                  <ExposureHierarchyVisualization exposures={exposures} />

                  {/* List of exposures */}
                  <h4>Exposures In Progress</h4>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: "10px",
                    }}
                  >
                    <button
                      className="link-btn"
                      onClick={() => setNewExposureModalVisible(true)}
                      disabled={!selectedClientId || isRestrictedClient}
                    >
                      Assign New Exposure
                    </button>
                  </div>
                  <div className="exposure">
                    <table className="tracker-table">
                      <thead>
                        <tr>
                          <th>Exposure Title</th>
                          <th style={{ width: "30px" }}>Fear</th>
                          <th style={{ width: "75px" }}>Confidence</th>
                          <th style={{ width: "110px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {exposures
                          .filter((exposure) => !exposure.completed)
                          .sort((a, b) => b.fearBelief - a.fearBelief)
                          .map((exposure) => {
                            // Get the color for the specific distress value
                            const backgroundColor =
                              distressColors[exposure.fearBelief];

                            return (
                              <tr
                                key={exposure.id}
                                style={{ backgroundColor, color: "black" }}
                              >
                                <td style={{ paddingLeft: "5px" }}>
                                  {exposure.title}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {exposure.fearBelief || "N/A"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {exposure.confidence || "N/A"}
                                </td>
                                <td
                                  style={{
                                    textAlign: "right",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "2px",
                                  }}
                                >
                                  <button
                                    className="exposure-btn"
                                    onClick={() => openModal(exposure.title)}
                                    style={{ backgroundColor: "#1e1e1e" }}
                                    title="View exposure history"
                                  >
                                    📋
                                  </button>
                                  <button
                                    className="exposure-btn"
                                    onClick={() => openNotesModal(exposure)}
                                    style={{ backgroundColor: "#1e1e1e" }}
                                    title="Clinical notes"
                                  >
                                    📝
                                  </button>
                                  <button
                                    className="exposure-btn"
                                    onClick={() =>
                                      openDistressSkillsModal(exposure)
                                    }
                                    style={{ backgroundColor: "#1e1e1e" }}
                                    title="Distress Tolerance Skills"
                                    disabled={isRestrictedClient}
                                  >
                                    🧘
                                  </button>

                                  <button
                                    className="exposure-btn"
                                    onClick={() =>
                                      confirmDeleteExposure(exposure)
                                    }
                                    style={{ backgroundColor: "#1e1e1e" }}
                                    title="Delete exposure"
                                    disabled={isRestrictedClient}
                                  >
                                    🗑️
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>

                    <h4>Completed Exposures</h4>
                    <table className="tracker-table">
                      <thead>
                        <tr>
                          <th>Exposure Title</th>
                          <th style={{ width: "30px" }}>Fear</th>
                          <th style={{ width: "75px" }}>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exposures
                          .filter((exposure) => exposure.completed)
                          .sort((a, b) => b.fearBelief - a.fearBelief)
                          .map((exposure) => {
                            const backgroundColor =
                              distressColors[exposure.fearBelief];

                            return (
                              <tr
                                key={exposure.id}
                                style={{ backgroundColor, color: "black" }}
                              >
                                <td
                                  style={{
                                    paddingLeft: "5px",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  {exposure.title}
                                </td>
                                <td
                                  style={{
                                    textAlign: "center",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  {exposure.fearBelief || "N/A"}
                                </td>
                                <td
                                  style={{
                                    textAlign: "center",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  {exposure.confidence || "N/A"}
                                </td>
                                <td
                                  style={{
                                    textAlign: "right",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "2px",
                                  }}
                                >
                                  <button
                                    className="exposure-btn"
                                    onClick={() =>
                                      confirmDeleteExposure(exposure)
                                    }
                                    style={{ backgroundColor: "#1e1e1e" }}
                                    title="Delete exposure"
                                    disabled={isRestrictedClient}
                                  >
                                    🗑️
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>

                  <ExposureProgressionTracker
                    exposures={exposures}
                    clientId={selectedClientId}
                  />
                </div>
              </div>

              <div class="tile tile-1" style={{ maxHeight: "800px" }}>
                <div class="tile-header">
                  <span class="icon">🎯</span>Trigger Tracker
                </div>
                <div class="tile-content">
                  <h3>Recorded Triggers</h3>
                  {data.length > 0 ? (
                    <table className="tracker-table">
                      <thead>
                        <tr>
                          <th>Trigger</th>
                          <th>Thought</th>
                          <th>Compulsion</th>
                          <th>Relief</th>
                          <th style={{ width: "30px" }}>Fear</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((entry, index) => {
                          const backgroundColor =
                            distressTriggerColors[entry.distress];
                          return (
                            <tr
                              key={index}
                              style={{ backgroundColor, color: "black" }}
                            >
                              <td style={{ paddingLeft: "5px" }}>
                                {entry.trigger}
                              </td>
                              <td>{entry.thought}</td>
                              <td>{entry.compulsion}</td>
                              <td>{entry.relief}</td>
                              <td style={{ fontSize: "15px" }}>
                                {entry.distress}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: "#4caf50" }}>
                      No triggers recorded yet.
                    </p>
                  )}
                </div>
              </div>

              <div className="tile tile-1" style={{ maxHeight: "760px" }}>
                <div className="tile-header">
                  <span className="icon">⏱</span>Goals
                </div>
                <div className="tile-content">
                  <br />
                  <div className="goal-content">
                    <label>
                      Working Life
                      <textarea
                        name="workingLife"
                        value={goals.workingLife}
                        style={{
                          backgroundColor: "#575656",
                          color: "#fff",
                          border: "1px solid #ccc",
                        }}
                      />
                    </label>
                    <label>
                      Home Life
                      <textarea
                        name="homeLife"
                        value={goals.homeLife}
                        style={{
                          backgroundColor: "#575656",
                          color: "#fff",
                          border: "1px solid #ccc",
                        }}
                      />
                    </label>
                    <label>
                      Social Life
                      <textarea
                        name="socialLife"
                        value={goals.socialLife}
                        style={{
                          backgroundColor: "#575656",
                          color: "#fff",
                          border: "1px solid #ccc",
                        }}
                      />
                    </label>
                    <label>
                      Relationships
                      <textarea
                        name="relationships"
                        value={goals.relationships}
                        style={{
                          backgroundColor: "#575656",
                          color: "#fff",
                          border: "1px solid #ccc",
                        }}
                      />
                    </label>
                    <label>
                      Personal Leisure Time
                      <textarea
                        name="personalLeisureTime"
                        value={goals.personalLeisureTime}
                        style={{
                          backgroundColor: "#575656",
                          color: "#fff",
                          border: "1px solid #ccc",
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="tile tile-1" style={{ height: "420px" }}>
                <div className="tile-header">
                  <span className="icon">😊</span> Mood Tracker
                </div>
                <div className="tile-content">
                  <div className="status-row">
                    <span className="icon">😊</span>
                    <span id="status-label-1">{emojiPercentages[1] || 0}%</span>
                    <div ref={statusBar1Ref} className="status-bar"></div>
                  </div>
                  <div className="status-row">
                    <span className="icon">🙂</span>
                    <span id="status-label-2">{emojiPercentages[2] || 0}%</span>
                    <div ref={statusBar2Ref} className="status-bar"></div>
                  </div>
                  <div className="status-row">
                    <span className="icon">😟</span>
                    <span id="status-label-3">{emojiPercentages[3] || 0}%</span>
                    <div ref={statusBar3Ref} className="status-bar"></div>
                  </div>
                  <div className="status-row">
                    <span className="icon">😫</span>
                    <span id="status-label-4">{emojiPercentages[4] || 0}%</span>
                    <div ref={statusBar4Ref} className="status-bar"></div>
                  </div>

                  <div className="container1-mood">
                    <div className="calendar-grid">{renderGrid()}</div>
                  </div>
                </div>
              </div>

              <div class="tile tile-1">
                <div class="tile-header">
                  <span class="icon">⏱️</span>SUDS Tracker
                </div>
                <div class="tile-content">
                  {allHistoricalData.length > 0 ? (
                    allHistoricalData.map((ritualData, ritualIndex) => {
                      //const summary = calculateSummary(ritualData); // Get the summary for this ritual
                      if (ritualData.trigger === "rituals") {
                        return null; // Do not render anything if the trigger is 'rituals'
                      }
                      return (
                        <div key={ritualIndex} className="ritual-graph">
                          <h3>
                            Recorded anxiety levels for{" "}
                            <span className="fear-color">
                              {ritualData.trigger}
                            </span>
                          </h3>
                          <div
                            className="responsive-wrapper"
                            style={{ marginRight: "20px" }}
                          >
                            <ResponsiveContainer width="100%" height={300}>
                              <LineChart>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  type="number"
                                  dataKey="time"
                                  tickFormatter={(value) => `${value} min`}
                                  label={{
                                    value: "Time (min)",
                                    position: "insideBottomRight",
                                    offset: -5,
                                  }}
                                />
                                <YAxis
                                  domain={[0, 100]}
                                  ticks={[0, 20, 40, 60, 80, 100]}
                                  label={{
                                    value: "Anxiety Level",
                                    angle: -90,
                                    position: "insideLeft",
                                  }}
                                />
                                <Tooltip />
                                <Legend />

                                <ReferenceLine y={80} stroke="red" />
                                <ReferenceLine y={20} stroke="green" />

                                {ritualData.data.map(
                                  (session, sessionIndex) => {
                                    return (
                                      <Line
                                        key={sessionIndex}
                                        data={session}
                                        type="monotone"
                                        dataKey="level"
                                        stroke={
                                          lineColors[
                                            sessionIndex % lineColors.length
                                          ]
                                        }
                                        strokeWidth={2}
                                        dot={<CustomDot />}
                                        name={`Session ${sessionIndex + 1}`}
                                      />
                                    );
                                  }
                                )}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          {/* Trend, Comparative, and Progress summary for this ritual */}
                        </div>
                      );
                    })
                  ) : (
                    <p style={{ textAlign: "center", color: "#4caf50" }}>
                      No history available.
                    </p>
                  )}
                </div>
              </div>

              <div className="tile tile-2">
                <div className="tile-header">
                  <span className="icon">💬</span> Messages
                </div>
                <div className="tile-content">
                  <p>Stay in touch with your patient.</p>
                  <div className="message-board">
                    <div className="messages-list">
                      {messages
                        .slice()
                        .reverse()
                        .map((msg, index) => (
                          <div
                            key={index}
                            className={`message ${msg.messageType}`}
                          >
                            <p>
                              <b>{msg.messageType === "therapist" && "You:"}</b>
                              <b>
                                {msg.messageType === "patient" && "Patient: "}
                              </b>
                              <b>
                                {msg.messageType === "admin" &&
                                  "OCDSereniry Admin: "}
                              </b>
                              <br />
                              <i>{msg.message}</i>
                            </p>
                            <span>
                              {new Date(
                                msg.timestamp.seconds * 1000
                              ).toLocaleTimeString()}
                            </span>
                          </div>
                        ))}
                    </div>

                    <div className="send-message">
                      <textarea
                        value={newMessage}
                        onChange={handleInputChangeGoal}
                        placeholder="Type your message here..."
                        style={{
                          backgroundColor: "#575656",
                          color: "#fff",
                          border: "1px solid #ccc",
                        }}
                      ></textarea>
                      <button
                        className="link-btn"
                        onClick={sendMessage}
                        disabled={isRestrictedClient}
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="therapist-dashboard">
        <h2 className="dashboard-title">💡 Share Your Suggestions</h2>
        <p>
          Is there a tool, feature, or method you’d like us to implement ? Let
          us know your ideas so we can make the app better for you and your
          patients.
        </p>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Type your suggestion here..."
          rows="5"
          style={{
            width: "50%",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            marginBottom: "10px",
            backgroundColor: "#575656",
            color: "white",
          }}
        ></textarea>
        <br />
        <button
          onClick={handleFeedbackSubmit}
          disabled={loading}
          style={{ cursor: loading ? "not-allowed" : "pointer" }}
          className="link-btn"
        >
          {loading ? "Submitting..." : "Submit Feedback"}
        </button>
      </div>
      {modalVisible && modal} {/* Render the modal if modalVisible is true */}
      {newExposureModalVisible && newExposureModal}
      {notesModalVisible && clinicalNotesModal}
      {deleteConfirmVisible && deleteConfirmationModal}
      {distressSkillsModalVisible && distressSkillsModal}
      {newExposureModalVisible && newExposureModal}
      {notesModalVisible && clinicalNotesModal}
      <Footer />
    </>
  );
};

export default TherapistDashboard;
